import * as React from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Brain } from 'lucide-react';
import apiService from '../../services/api';
import * as tokenStorage from '../../utils/tokenStorage';
import { directionForLanguage } from '../../i18n/config';
import {
  CrossCheckClause,
  CrossCheckMatter,
  RawCrossCheckClause,
  CrossCheckDocumentType,
  mapRawClause,
  formatClauseNumberForDisplay,
} from '../../types/crossCheck';
import {
  hasSavedAgentAutoResult,
  AGENT_GATE_RETRY_MAX_ATTEMPTS,
  AGENT_GATE_RETRY_MS,
  AGENT_PHASE_ORDER,
  type AgentPhaseState,
  buildAskAgentContextForClause,
  computeAgentPhaseProgressFromClauses,
  getAgentSubmitFailures,
  isAgentEditPending,
  isAgentSubmitPending,
  isClauseEligibleForAgentAutoApply,
  INITIAL_AGENT_PHASE_PROGRESS,
  agentPhasePercent,
  clauseMatchesAutoCloseCriteria,
  reduceAgentPhaseProgress,
  runAgentAutoApplyPipeline,
  type AgentPhaseKey,
  type AgentPhaseProgress,
  type PipelinePauseInfo,
  type ReviewMode,
} from '../../utils/agentAutoApply';
import { buildClauseHighlightSearchText } from '../../utils/pdfClauseHighlight';
import { CrossCheckClauseCard } from './CrossCheckClauseCard';
import { loadCustomQuickResponses } from '../../utils/customQuickResponsePrefs';
import { BrainShareReviewModal } from './BrainShareReviewModal';
import './BrainShareReviewModal.css';
import type { SubmitRevertContext } from '../../word/trackChanges/RedlineInserter';
import {
  isRicherSubmitRevertContext,
  submitRevertContextFromSubmissionStatus,
} from '../../utils/submitRevertContext';
import { AnalyzeAgainModal, AnalyzeAgainModalVariant } from './AnalyzeAgainModal';
import { BulkCloseConfirmModal } from './BulkCloseConfirmModal';
import { PastNegotiationSourcePdfPanel } from './PastNegotiationSourcePdfPanel';
import { PlaybookAgentBesidePanel } from './PlaybookAgentBesidePanel';
import { buildPlaybookContextFromRows } from '../../utils/playbookContext';
import {
  applyClauseEditorFilters,
  collectPastNegotiationAuthors,
  collectPastNegotiationYears,
  formatAuthorFilterLabel,
  formatSimilarityFilterLabel,
  formatTimeFilterLabel,
  isClauseClosable,
  type PastNegotiationTimeFilter,
  SIMILARITY_FILTER_MAX,
  SIMILARITY_FILTER_MIN,
  SIMILARITY_FILTER_PRESETS,
} from '../../utils/clauseEditorFilters';
import {
  acceptClausesSequentially,
  partitionClosableByTrackChanges,
} from '../../utils/clauseBulkCloseUtils';
import {
  buildClauseAskAgentPrefix,
  clauseHasAskAgentGrounding,
  clauseNeedsAskAgentContextSourceChoice,
  combineAskAgentContext,
  detectAskAgentLanguage,
  getAskAgentCopy,
  getAskAgentInitialQuestion,
  getAskAgentInitialQuestionForSource,
  getAskAgentNoMatchesNotice,
  getAskAgentNoMatchesNoPlaybookNotice,
  type AskAgentContextSource,
  type AskAgentDomain,
} from '../../utils/clauseAskAgentContext';
import type { SupportedLanguage } from '../../utils/languageDetection';
import { streamAiSuggestionFromChat } from '../../utils/crossCheckAiSuggestion';
import type { PlaybookAgentChatTurn } from './PlaybookAgentBesidePanel';
import type { PlaybookArchiveRow } from '../../utils/playbookRecordIndex';
import {
  buildMatchRankToArchiveRecordNum,
  buildPlaybookRecordNumberById,
} from '../../utils/playbookRecordIndex';
import { DocumentReader } from '../../word/api/DocumentReader';
import { DocumentStructureWatcher } from '../../word/api/DocumentStructureWatcher';
import { SelectionTracker } from '../../word/api/SelectionTracker';
import {
  getClientRequestSearchText,
  buildParagraphSyncEntries,
  extractClauseFromRowContext,
  resolveTableRowClauseNumber,
  resolveEffectiveParagraphIndex,
} from '../../utils/crossCheckDocumentUtils';
import { crossCheckSolutionFromDomain, type CrossCheckSolution } from '../../utils/crossCheckSolution';
import {
  clauseCardsHoldStage,
  crossCheckProgressSignature,
  isCrossCheckWorkflowTerminal,
  resolveMccWorkflowStepStatuses,
  type MccUiStepStatus,
  type MccWorkflowProgress,
} from '../../utils/crossCheckWorkflowStatus';
import { log } from '../../utils/logging';
import { recordStickyDecision, recordStickyLanding, type StickyLanding } from '../../utils/stickyDecisionLog';
import { pickStickyCardByText, stickySearchTextsForClause } from '../../utils/stickyCardMatch';
import { MIN_CLAUSE_SEARCH_LEN } from '../../word/api/paragraphSearchMatch';
import {
  isDebugLogPanelVisible,
  subscribeDebugLogPanelVisible,
  isStickyScrollToCardEnabled,
  subscribeStickyScrollToCardEnabled,
} from '../../utils/addInPreferences';
import {
  clearDebugLog,
  copyDebugLogToClipboard,
  debugLog,
  debugWarn,
  getRemoteDebugBufferedCount,
  getRemoteDebugSessionId,
  subscribeDebugLog,
} from '../../utils/RemoteDebugLog';
import './CrossCheckResultsView.css';

const SA_BESIDE_SPLIT_PCT_KEY = 'courtyard-word_sa-source-beside-split-pct';
const DEFAULT_BESIDE_PDF_PCT = 47;
const MIN_BESIDE_PDF_PCT = 22;
const MAX_BESIDE_PDF_PCT = 78;

function readStoredBesidePdfPct(): number {
  try {
    const n = parseFloat(localStorage.getItem(SA_BESIDE_SPLIT_PCT_KEY) || '');
    if (Number.isFinite(n) && n >= MIN_BESIDE_PDF_PCT && n <= MAX_BESIDE_PDF_PCT) {
      return Math.round(n);
    }
  } catch {
    /* private mode */
  }
  return DEFAULT_BESIDE_PDF_PCT;
}

/** Collect unique playbook row IDs referenced by past_agreement options (Construction Loan). */
function collectAllPastAgreementPlaybookIds(clauses: CrossCheckClause[]): string[] {
  const set = new Set<string>();
  for (const c of clauses) {
    const pa = (c as any).past_agreement;
    if (!pa || !Array.isArray(pa.options)) continue;
    for (const opt of pa.options as { id?: string }[]) {
      const id = opt?.id;
      if (id && String(id).trim()) set.add(String(id).trim());
    }
  }
  return Array.from(set).sort();
}

// Tabs differ by domain:
//  - Master Cross Checks (and any non-CL flow): grouped by `documentType`.
//      'all' | 'sales_agreement' | 'appendix_a' | 'saved'
//  - Construction Loan: grouped by `matter` classification.
//      'all' | 'commercial' | 'legal' | 'other'
type DocTabId = 'all' | 'sales_agreement' | 'appendix_a' | 'saved';
type MatterTabId = 'all' | 'commercial' | 'legal' | 'other';
type TabId = DocTabId | MatterTabId;
type StepStatus = MccUiStepStatus;

/**
 * Outcome of a single loadResults() call, used by the polling loop to decide
 * whether to keep polling, back off, or stop. `authError` means re-auth is
 * required and polling must stop (prevents the tokenless 401 storm).
 */
type LoadResult = {
  ok: boolean;
  authError: boolean;
  progress?: MccWorkflowProgress;
};

export interface MccBesidePdfLayoutChangeDetail {
  /** True when the SA PDF viewer is open and portaled into TaskPane’s dock column. */
  docked: boolean;
  /** PDF column width % (for TaskPane grid); same as persisted beside split. */
  pdfWidthPct: number;
}

interface CrossCheckResultsViewProps {
  subProjectId: string;
  projectId?: string;
  /** Passed from TaskPane so the view can clear a stale pollingError and restart
   *  polling when the user logs back in after a forced/expired-token logout. */
  isAuthenticated?: boolean;
  /**
   * When set (MCC / Construction Loan in TaskPane), the SA PDF chrome is portaled
   * here so it spans the full clauses-view height beside Project Settings.
   */
  pdfBesideDockRef?: React.RefObject<HTMLDivElement | null>;
  /** Sibling column for the vertical resize handle (TaskPane). */
  besideHandleSlotRef?: React.RefObject<HTMLDivElement | null>;
  /** Width of this container for split drag math (usually `clauses-view`). */
  besideResizeOuterRef?: React.RefObject<HTMLElement | null>;
  /** Notifies TaskPane to toggle grid layout + CSS variable for PDF column width. */
  onMccBesidePdfLayoutChange?: (detail: MccBesidePdfLayoutChangeDetail) => void;
  /** TaskPane sets true after dock + handle slots mount (enables createPortal targets). */
  besidePdfChromeInDock?: boolean;
  /** Incremented when entering MCC/CL from the solutions list — scroll workflow steps into view. */
  workflowScrollRequestToken?: number;
  /** TaskPane MCC beside: scroll root for clause list when Ask Agent / SA PDF is docked. */
  besideRightScrollRef?: React.RefObject<HTMLElement | null>;
  /**
   * True when the user was offered the cross-check for this client project and
   * chose "later". An unwritten progress blob is indistinguishable from one, so
   * without this the view would wait on a run that is never going to start.
   */
  crossCheckStartDeclined?: boolean;
  /**
   * False while the view is kept, hidden, behind another tab of the pane. It keeps its state and follows
   * the server, but stops the work that follows the lawyer in Word: no document scan when Word regains
   * focus, no selection tracking, no step clock, and the agent run stops at its next step and goes on
   * when the view is shown again.
   */
  active?: boolean;
}

/** Nearest scrollable ancestor (overflow auto/scroll with overflow content). */
function findScrollableAncestor(el: HTMLElement): HTMLElement | null {
  let parent = el.parentElement;
  while (parent) {
    const { overflowY } = getComputedStyle(parent);
    if (
      (overflowY === 'auto' || overflowY === 'scroll' || overflowY === 'overlay') &&
      parent.scrollHeight > parent.clientHeight + 1
    ) {
      return parent;
    }
    parent = parent.parentElement;
  }
  return null;
}

export const CrossCheckResultsView: React.FC<CrossCheckResultsViewProps> = ({
  subProjectId,
  projectId: projectIdProp,
  pdfBesideDockRef,
  besideHandleSlotRef,
  besideResizeOuterRef,
  onMccBesidePdfLayoutChange,
  besidePdfChromeInDock,
  workflowScrollRequestToken = 0,
  besideRightScrollRef,
  crossCheckStartDeclined = false,
  isAuthenticated = true,
  active = true,
}) => {
  const { t, i18n } = useTranslation('mcc');
  const chromeDir = directionForLanguage(i18n.language);
  const [projectId, setProjectId] = React.useState<string | undefined>(projectIdProp);
  const [parentProjectName, setParentProjectName] = React.useState<string | null>(null);
  const [detectedDomain, setDetectedDomain] = React.useState<'construction-loan' | 'real-estate' | null>(null);
  const crossCheckSolution = crossCheckSolutionFromDomain(detectedDomain);
  // Ref mirror so loadResults / sync can read the current domain without dep churn.
  const detectedDomainRef = React.useRef(detectedDomain);
  const crossCheckSolutionRef = React.useRef<CrossCheckSolution>(crossCheckSolution);
  React.useEffect(() => {
    detectedDomainRef.current = detectedDomain;
    crossCheckSolutionRef.current = crossCheckSolution;
  }, [detectedDomain, crossCheckSolution]);
  // The playbook archive endpoints take a project id, so they cannot read the
  // sub-project domain cache and were probing construction-loan for Master
  // Cross Check projects on every run. We already know the domain here; tell
  // the API layer once, so the probe never happens rather than being learned
  // from a 400.
  const resolvedProjectIdForDomain = projectId ?? projectIdProp;
  React.useEffect(() => {
    if (detectedDomain && resolvedProjectIdForDomain) {
      apiService.rememberProjectDomain(resolvedProjectIdForDomain, detectedDomain);
    }
  }, [detectedDomain, resolvedProjectIdForDomain]);
  const [clauses, setClauses] = React.useState<CrossCheckClause[]>([]);
  /** Start true so we never flash the default "step 1 running" strip before the first fetch. */
  const [loading, setLoading] = React.useState(() => Boolean(subProjectId));
  const [error, setError] = React.useState<string | null>(null);
  /** Set during a silent poll failure so we can show a subtle indicator without wiping the existing results. */
  const [pollingError, setPollingError] = React.useState(false);
  /** True when polling has stopped after too many consecutive errors — shows the "retry" banner. */
  const [pollingStopped, setPollingStopped] = React.useState(false);
  /** Incrementing this restarts the poll effect (used by the "retry" button). */
  const [pollRetryKey, setPollRetryKey] = React.useState(0);
  const [syncingIndices, setSyncingIndices] = React.useState(false);
  const [showDebugLogPanel, setShowDebugLogPanel] = React.useState(() => isDebugLogPanelVisible());
  const [remoteDebugEntryCount, setRemoteDebugEntryCount] = React.useState(() => getRemoteDebugBufferedCount());
  const [remoteDebugCopyStatus, setRemoteDebugCopyStatus] = React.useState<string | null>(null);
  const [remoteDebugCopying, setRemoteDebugCopying] = React.useState(false);

  // When the user logs back in after a forced/expired-token logout the poll loop
  // has already exited (it stopped on the auth-failure path) and `pollingError`
  // is still true, showing a permanent "Reconnecting…" until the add-in is
  // refreshed.  Watching the isAuthenticated prop for a false→true edge lets us
  // clear the stale banner and restart polling without any user intervention.
  const prevIsAuthenticatedRef = React.useRef(isAuthenticated);
  React.useEffect(() => {
    const wasAuthenticated = prevIsAuthenticatedRef.current;
    prevIsAuthenticatedRef.current = isAuthenticated;

    if (!wasAuthenticated && isAuthenticated) {
      // Re-authenticated: clear stale reconnecting state and restart the poll.
      setPollingError(false);
      setPollingStopped(false);
      setPollRetryKey((k) => k + 1);
      debugLog('CrossCheckResultsView: re-authenticated — restarting poll');
    }
  }, [isAuthenticated]);

  React.useEffect(() => {
    if (!isAuthenticated) return;
    void loadCustomQuickResponses(true);
  }, [isAuthenticated]);

  React.useEffect(
    () =>
      subscribeDebugLogPanelVisible(() => {
        setShowDebugLogPanel(isDebugLogPanelVisible());
      }),
    [],
  );

  React.useEffect(
    () =>
      subscribeDebugLog(() => {
        setRemoteDebugEntryCount(getRemoteDebugBufferedCount());
      }),
    [],
  );

  React.useEffect(() => {
    if (!remoteDebugCopyStatus) return;
    const t = window.setTimeout(() => setRemoteDebugCopyStatus(null), 5000);
    return () => window.clearTimeout(t);
  }, [remoteDebugCopyStatus]);
  const [activeTab, setActiveTab] = React.useState<TabId>('all');
  const [filterFinalVersion, setFilterFinalVersion] = React.useState(false);
  const [filterMinSimilarity, setFilterMinSimilarity] = React.useState<number | null>(null);
  const [similarityPanelOpen, setSimilarityPanelOpen] = React.useState(false);
  const [similaritySliderValue, setSimilaritySliderValue] = React.useState(SIMILARITY_FILTER_MAX);
  const similarityFilterRef = React.useRef<HTMLDivElement | null>(null);
  const [filterSelectedAuthors, setFilterSelectedAuthors] = React.useState<Set<string> | null>(null);
  const [authorsPanelOpen, setAuthorsPanelOpen] = React.useState(false);
  const authorsFilterRef = React.useRef<HTMLDivElement | null>(null);
  const [filterSelectedYears, setFilterSelectedYears] = React.useState<PastNegotiationTimeFilter>(null);
  const [timePanelOpen, setTimePanelOpen] = React.useState(false);
  const timeFilterRef = React.useRef<HTMLDivElement | null>(null);
  const [bulkClosing, setBulkClosing] = React.useState(false);
  const [bulkOpening, setBulkOpening] = React.useState(false);
  const [bulkClosePrompt, setBulkClosePrompt] = React.useState<{
    toLock: CrossCheckClause[];
    withTrackChanges: CrossCheckClause[];
    withoutTrackChanges: CrossCheckClause[];
  } | null>(null);
  const [bulkCloseBusyMessage, setBulkCloseBusyMessage] = React.useState<string | null>(null);
  const [updatingLockId, setUpdatingLockId] = React.useState<string | null>(null);
  const [submitRevertContextByClauseId, setSubmitRevertContextByClauseId] = React.useState<
    Record<string, SubmitRevertContext>
  >({});

  const syncSubmitRevertContextFromClauses = React.useCallback((nextClauses: CrossCheckClause[]) => {
    setSubmitRevertContextByClauseId((prev) => {
      const next = { ...prev };
      const activeIds = new Set(nextClauses.map((c) => c.id));
      for (const clause of nextClauses) {
        const serverCtx = submitRevertContextFromSubmissionStatus(clause.submission_status);
        if (serverCtx && isRicherSubmitRevertContext(serverCtx, next[clause.id])) {
          next[clause.id] = serverCtx;
        } else if (!clause.submission_status?.response_submitted) {
          delete next[clause.id];
        }
      }
      for (const id of Object.keys(next)) {
        if (!activeIds.has(id)) delete next[id];
      }
      return next;
    });
  }, []);

  React.useEffect(() => {
    if (!clauses.length) return;
    syncSubmitRevertContextFromClauses(clauses);
  }, [clauses, syncSubmitRevertContextFromClauses]);
  const [mutatingClauseIds, setMutatingClauseIds] = React.useState<Record<string, boolean>>({});
  const [analyzeAgainClause, setAnalyzeAgainClause] = React.useState<CrossCheckClause | null>(null);
  const [analyzeAgainVariant, setAnalyzeAgainVariant] =
    React.useState<AnalyzeAgainModalVariant>('mcc');
  const [actionBanner, setActionBanner] = React.useState<{ type: 'success' | 'error' | 'loading'; message: string } | null>(null);
  const [brainSharePendingCount, setBrainSharePendingCount] = React.useState(0);
  const [brainShareTotalCount, setBrainShareTotalCount] = React.useState(0);
  const [showBrainShareReviewModal, setShowBrainShareReviewModal] = React.useState(false);
  const [workflowStep, setWorkflowStep] = React.useState<number | null>(null);
  const [workflowStatus, setWorkflowStatus] = React.useState<string | null>(null);
  /** Full `cross_check_progress` from API — `status`/`workflow_step` alone miss some terminal shapes. */
  const [workflowProgressBlob, setWorkflowProgressBlob] = React.useState<Record<string, unknown> | null>(null);
  const [timerNowMs, setTimerNowMs] = React.useState<number>(Date.now());
  const stickyHeaderRef = React.useRef<HTMLDivElement | null>(null);
  const resultsMainColumnRef = React.useRef<HTMLDivElement | null>(null);
  const clauseCardHeaderRefs = React.useRef<Map<string, HTMLDivElement>>(new Map());
  const setClauseCardHeaderRef = React.useCallback(
    (clauseId: string) => (el: HTMLDivElement | null) => {
      if (el) clauseCardHeaderRefs.current.set(clauseId, el);
      else clauseCardHeaderRefs.current.delete(clauseId);
    },
    [],
  );
  /** Scroll target for "Identify sides / …" when entering MCC/CL from the solutions list. */
  const workflowStepsScrollAnchorRef = React.useRef<HTMLDivElement | null>(null);
  const consumedWorkflowScrollTokenRef = React.useRef(0);
  /** Only scroll after a real tab change — Strict Mode runs mount effects twice; a one-shot "skip first" ref still fires on the second run. */
  const prevActiveTabForScrollRef = React.useRef<TabId | null>(null);
  const syncInFlightRef = React.useRef(false);
  const lastSyncedSignatureRef = React.useRef<string>('');
  const prevClauseIdsSignatureRef = React.useRef<string>('');
  const lastSyncAttemptMsRef = React.useRef<number>(0);
  // mountMs is used as the fallback start time for step1 when the component mounts
  // before the first poll arrives (prevents the timer showing 00:00 initially).
  const mountMsRef = React.useRef<number>(Date.now());
  const stepTimingRef = React.useRef<{
    step1StartMs: number | null;
    step1ElapsedSec: number;
    step2StartMs: number | null;
    step2ElapsedSec: number;
  }>({
    step1StartMs: null,
    step1ElapsedSec: 0,
    step2StartMs: null,
    step2ElapsedSec: 0,
  });

  /**
   * Re-sync paragraph indices and clause numbers from the live Word document
   * using the current clauses state. Called both from loadResults() and from
   * the DocumentStructureWatcher when a structural change (paragraph added /
   * deleted) is detected — without triggering a full server reload.
   */
  const runParagraphSync = React.useCallback((currentClauses: CrossCheckClause[]) => {
    if (syncInFlightRef.current) return;

    const entries = buildParagraphSyncEntries(currentClauses, {
      solution: crossCheckSolutionRef.current,
    });

    if (entries.length === 0) return;

    syncInFlightRef.current = true;
    lastSyncAttemptMsRef.current = Date.now();
    DocumentReader.invalidateOoxmlParagraphTextsCache();
    // Invalidate the signature so the next loadResults() also re-runs sync.
    lastSyncedSignatureRef.current = '';
    setSyncingIndices(true);

    debugLog('CrossCheckResultsView: paragraph sync (structure change)', {
      entryCount: entries.length,
    });

    DocumentReader.syncParagraphIndicesFromDocument(entries)
      .then(({ indices, clauseNumbers }) => {
        debugLog('CrossCheckResultsView: paragraph sync (structure change)', {
          matched: Object.keys(indices).length,
          entries: entries.length,
        });

        const entryIds = new Set(entries.map((e) => e.id));
        setClauses((prev) =>
          prev.map((c) => {
            const nextIndex = indices[c.id];
            const nextClauseNumber = clauseNumbers[c.id];
            const wasSyncAttempted = entryIds.has(c.id);
            const shouldClearStaleIndex = wasSyncAttempted && nextIndex === undefined;
            const changed =
              nextIndex !== undefined || nextClauseNumber !== undefined || shouldClearStaleIndex;

            if (!changed) return c;

            return {
              ...c,
              ...(shouldClearStaleIndex && { paragraph_index: undefined }),
              ...(nextIndex !== undefined && { paragraph_index: nextIndex }),
              ...(nextClauseNumber !== undefined && { clause_number: nextClauseNumber }),
            };
          })
        );
      })
      .catch((err) => {
        console.warn('[CrossCheckResultsView] Paragraph sync (structure change) failed:', err);
      })
      .finally(() => {
        syncInFlightRef.current = false;
        setSyncingIndices(false);
      });
  }, []);

  // Re-sync paragraph indices at the natural moments the user looks at the
  // panel: when the add-in window regains focus or becomes visible. No
  // background polling — zero overhead while the user is typing in Word.
  const clausesRef = React.useRef<CrossCheckClause[]>(clauses);
  React.useEffect(() => {
    clausesRef.current = clauses;
  }, [clauses]);
  const activeRef = React.useRef(active);
  React.useEffect(() => {
    activeRef.current = active;
  }, [active]);

  React.useEffect(() => {
    const watcher = DocumentStructureWatcher.getInstance();
    const unsubscribe = watcher.subscribe(() => {
      // Hidden behind another tab: the sync runs when the view is shown again.
      if (!activeRef.current) return;
      debugLog('CrossCheckResultsView: panel attention — re-syncing paragraph indices');
      runParagraphSync(clausesRef.current);
    });
    return unsubscribe;
  }, [runParagraphSync]);

  const toRawClauseFromIdentifiedRequest = React.useCallback((req: any, idx: number): RawCrossCheckClause => {
    const tableRowSupport = crossCheckSolutionRef.current === 'mcc';
    const paragraphIndex =
      req?.paragraph_index ??
      req?.track_changes?.paragraph_index ??
      undefined;
    const paragraphXml =
      req?.track_changes?.paragraph_xml ||
      req?.change_history?.paragraph_xml ||
      '';
    const editedText = req?.edited_text || req?.text || '';
    const originalText = req?.original_text || '';
    const isTableRow = tableRowSupport && req?.track_changes?.is_table_row === true;
    // Each table-row card is one TC paragraph — use its paragraph_xml like body Module B.
    const clientRequestValue =
      typeof paragraphXml === 'string' && paragraphXml.trim().length > 0
        ? paragraphXml
        : editedText || originalText || '';

    let clauseNumber = req?.clause_number;
    const rowContextText = req?.track_changes?.row_context_text;
    if (tableRowSupport) {
      clauseNumber = resolveTableRowClauseNumber(clauseNumber, req?.track_changes, {
        solution: crossCheckSolutionRef.current,
      });
    }
    if (
      tableRowSupport &&
      isTableRow &&
      (!String(clauseNumber ?? '').trim() || String(clauseNumber).trim() === 'לא נמצא')
    ) {
      const fromRowContext = extractClauseFromRowContext(
        typeof rowContextText === 'string' ? rowContextText : '',
      );
      if (fromRowContext) {
        clauseNumber = fromRowContext;
      }
    }

    const trackChangesParts =
      tableRowSupport ? req?.track_changes?.track_changes_parts : undefined;
    const mergedTrackChanges =
      req?.track_changes && typeof req.track_changes === 'object'
        ? {
            ...req.track_changes,
            ...(Array.isArray(trackChangesParts) && trackChangesParts.length > 0
              ? { track_changes_parts: trackChangesParts }
              : {}),
          }
        : req?.track_changes;

    return {
      id: String(req?.id ?? `identified_${idx + 1}`),
      clause_number: clauseNumber,
      documentType: req?.documentType || 'sales_agreement',
      // Match final cross-check behavior: prefer rendering XML for Client Request.
      client_request: clientRequestValue,
      original_text: originalText,
      edited_text: editedText,
      track_changes: mergedTrackChanges,
      participants: req?.participants,
      module_type: req?.module_type || 'module_b',
      paragraph_index: paragraphIndex != null ? Number(paragraphIndex) : undefined,
      track_changes_interpretation: req?.track_changes_interpretation,
      refered_file: req?.refered_file,
      text: req?.text,
      side: req?.side,
      // Construction Loan groups requests by `matter` (commercial / legal /
      // other) instead of `documentType`, so it must be carried through here.
      matter: req?.matter,
    };
  }, []);

  const loadResults = React.useCallback(async (silent = false): Promise<LoadResult> => {
    if (!subProjectId) {
      if (!silent) setLoading(false);
      return { ok: false, authError: false };
    }
    if (!silent) {
      setLoading(true);
      setWorkflowStep(null);
      setWorkflowStatus(null);
      setWorkflowProgressBlob(null);
    }
    setError(null);

    let result: LoadResult = { ok: false, authError: false };

    if (!silent) {
      debugLog('CrossCheckResultsView: loading results', { subProjectId });
    }

    try {
      const response = await apiService.getSubProjectDetails(subProjectId, detectedDomainRef.current ?? undefined);

      if (response.success && response.data) {
        if (response.data.parent_project_id && !projectIdProp) {
          setProjectId(response.data.parent_project_id);
        }
        const pname =
          response.data.parent_project_name ||
          response.data.project_name ||
          response.data.name;
        if (typeof pname === 'string' && pname.trim()) {
          setParentProjectName(pname.trim());
        }
        // Capture the domain detected by getSubProjectDetails so that playbook
        // resolution calls are routed to the correct backend prefix.
        const responseDomain = (response as any).detected_domain;
        if (responseDomain === 'construction-loan' || responseDomain === 'real-estate') {
          setDetectedDomain(responseDomain);
        }
        const progress = response.data.cross_check_progress || null;
        const progressObj =
          progress && typeof progress === 'object' && !Array.isArray(progress)
            ? (progress as Record<string, unknown>)
            : null;
        setWorkflowProgressBlob(progressObj);
        setWorkflowStep(
          typeof progressObj?.workflow_step === 'number'
            ? progressObj.workflow_step
            : progress?.workflow_step ?? null
        );
        setWorkflowStatus(
          typeof progressObj?.status === 'string' ? progressObj.status : progress?.status ?? null
        );
        result = { ok: true, authError: false, progress: progressObj };

        const rawCrossCheckResults: RawCrossCheckClause[] = Array.isArray(response.data.cross_checks_results)
          ? response.data.cross_checks_results
          : [];
        // Moves archive the prior card as `stale: true` snapshots in cross_checks_results.
        // Hide those rows in the UI (dashboard does the same) so reload does not show a
        // ghost copy of the clause in its original tab.
        const activeCrossCheckResults = rawCrossCheckResults.filter((c) => !c.stale);
        const rawIdentifiedRequests = Array.isArray(response.data?.client_requests_assignments_results?.requests)
          ? response.data.client_requests_assignments_results.requests
          : [];

        // Show identified requests immediately (Step 1/2) when full cross-check
        // results are not ready yet; once cross_checks_results exists, prefer it.
        const raw: RawCrossCheckClause[] = activeCrossCheckResults.length > 0
          ? activeCrossCheckResults
          : rawIdentifiedRequests.map(toRawClauseFromIdentifiedRequest);

        // Build a set of "our_side" author names from sub-project metadata.
        // Clause cards whose every participant is our_side are hidden – they
        // represent our own redlines and should never appear in the results.
        // API returns sub_project_metadata (not metadata); without this, sideAssignments was always {}.
        const meta = response.data?.sub_project_metadata ?? response.data?.metadata ?? {};
        const metaReviewMode = meta?.review_mode;
        if (
          metaReviewMode === 'first_pass_legal_review' ||
          metaReviewMode === 'review_and_respond_redlines'
        ) {
          setReviewMode(metaReviewMode);
        }
        const sideAssignments: Record<string, string> =
          meta?.side_assignments_data?.side_assignments ?? {};
        const isOurSideOnly = (clause: RawCrossCheckClause): boolean => {
          const participants: string[] = Array.isArray((clause as any).participants)
            ? (clause as any).participants
            : [];
          if (!participants.length) return false;
          return participants.every((p) => sideAssignments[p] === 'our_side');
        };

        const filtered = Object.keys(sideAssignments).length > 0
          ? raw.filter((c) => !isOurSideOnly(c))
          : raw;
        const enableTableRowSupport = crossCheckSolutionRef.current === 'mcc';
        const mapOptions = { enableTableRowSupport };
        const syncCtx = { solution: crossCheckSolutionRef.current };
        const mapped = filtered.map((rawClause) => mapRawClause(rawClause, mapOptions));

        // Merge with previous clauses so later polling refreshes do not downgrade
        // already-synced client-side clause numbers (e.g. "8.7.1") back to
        // backend placeholders such as "לא נמצא".
        const isUnknownClauseNumber = (value: unknown): boolean => {
          const v = String(value ?? '').trim();
          return (
            !v ||
            v === 'לא נמצא' ||
            v.toLowerCase() === 'not found' ||
            v.toLowerCase() === 'n/a' ||
            v === '-'
          );
        };

        const clauseIdsSignature = mapped
          .map((c) => c.id)
          .sort()
          .join('|');
        if (clauseIdsSignature !== prevClauseIdsSignatureRef.current) {
          prevClauseIdsSignatureRef.current = clauseIdsSignature;
          lastSyncedSignatureRef.current = '';
        }

        setClauses((prev) => {
          if (!prev.length || !silent) return mapped;
          const prevById = new Map(prev.map((c) => [c.id, c]));
          const merged = mapped.map((nextClause) => {
            const prevClause = prevById.get(nextClause.id);
            if (!prevClause) return nextClause;

            const keepPrevClauseNumber =
              !isUnknownClauseNumber(prevClause.clause_number) &&
              isUnknownClauseNumber(nextClause.clause_number);
            // If we already synced a concrete clause number for the same paragraph,
            // keep it authoritative over backend placeholder numbering (e.g. 4.7/6.4).
            const keepPrevSyncedClauseNumber =
              !isUnknownClauseNumber(prevClause.clause_number) &&
              !isUnknownClauseNumber(nextClause.clause_number) &&
              String(prevClause.clause_number) !== String(nextClause.clause_number) &&
              prevClause.paragraph_index != null &&
              nextClause.paragraph_index != null &&
              Number(prevClause.paragraph_index) === Number(nextClause.paragraph_index);
            const keepPrevParagraphIndex =
              prevClause.paragraph_index != null && nextClause.paragraph_index == null;
            const keepPrevAskAgentAuto =
              Boolean(prevClause.ask_agent_auto_result) && !nextClause.ask_agent_auto_result;

            if (
              !keepPrevClauseNumber &&
              !keepPrevSyncedClauseNumber &&
              !keepPrevParagraphIndex &&
              !keepPrevAskAgentAuto
            ) {
              return nextClause;
            }

            return {
              ...nextClause,
              ...((keepPrevClauseNumber || keepPrevSyncedClauseNumber) && {
                clause_number: prevClause.clause_number,
              }),
              ...(keepPrevParagraphIndex && { paragraph_index: prevClause.paragraph_index }),
              ...(keepPrevAskAgentAuto && {
                ask_agent_auto_result: prevClause.ask_agent_auto_result,
              }),
            };
          });

          return merged;
        });

        // Sync paragraph indices in the document (non-blocking): find correct indices by content.
        // Guard against repeated polling-driven sync loops: only sync when source clauses changed.
        const entries = buildParagraphSyncEntries(mapped, syncCtx);
        const syncSignature = JSON.stringify(
          entries.map((e) => [e.id, e.searchText.slice(0, 200)])
        );
        const hasUnresolvedParagraphIndex = mapped.some(
          (c) =>
            getClientRequestSearchText(c, syncCtx).trim().length > 0 &&
            (c as any).paragraph_index == null
        );
        const canRetryUnresolvedSync =
          hasUnresolvedParagraphIndex &&
          Date.now() - lastSyncAttemptMsRef.current > 10000;
        const forceSyncForExplicitLoad = !silent;
        const shouldRunSync =
          entries.length > 0 &&
          !syncInFlightRef.current &&
          (forceSyncForExplicitLoad || syncSignature !== lastSyncedSignatureRef.current || canRetryUnresolvedSync);

        if (shouldRunSync) {
          syncInFlightRef.current = true;
          lastSyncAttemptMsRef.current = Date.now();
          lastSyncedSignatureRef.current = syncSignature;
          setSyncingIndices(true);

          debugLog('CrossCheckResultsView: paragraph sync starting', {
            subProjectId,
            entryCount: entries.length,
            forceSyncForExplicitLoad,
            hasUnresolvedParagraphIndex,
            canRetryUnresolvedSync,
            syncSignatureLen: syncSignature.length,
            clausesBeforeSync: mapped.map((c) => ({
              id: c.id,
              clauseNumber: c.clause_number,
              paragraphIndex: c.paragraph_index ?? null,
              searchTextLen: getClientRequestSearchText(c, syncCtx).trim().length,
            })),
          });

          DocumentReader.syncParagraphIndicesFromDocument(entries)
            .then(({ indices, clauseNumbers }) => {
              debugLog('CrossCheckResultsView: paragraph sync', {
                matched: Object.keys(indices).length,
                entries: entries.length,
              });

              const entryIds = new Set(entries.map((e) => e.id));
              const syncChanges: Array<Record<string, unknown>> = [];
              setClauses((prev) =>
                prev.map((c) => {
                  const nextIndex = indices[c.id];
                  const nextClauseNumber = clauseNumbers[c.id];
                  const wasSyncAttempted = entryIds.has(c.id);
                  const shouldClearStaleIndex = wasSyncAttempted && nextIndex === undefined;
                  const changed = nextIndex !== undefined || nextClauseNumber !== undefined || shouldClearStaleIndex;

                  if (!changed) return c;

                  if (wasSyncAttempted) {
                    syncChanges.push({
                      clauseId: c.id,
                      clauseNumber: c.clause_number,
                      prevParagraphIndex: c.paragraph_index ?? null,
                      nextParagraphIndex: shouldClearStaleIndex ? null : (nextIndex ?? c.paragraph_index ?? null),
                      nextClauseNumber: nextClauseNumber ?? null,
                      clearedStaleIndex: shouldClearStaleIndex,
                    });
                  }

                  return {
                    ...c,
                    ...(shouldClearStaleIndex && { paragraph_index: undefined }),
                    ...(nextIndex !== undefined && { paragraph_index: nextIndex }),
                    ...(nextClauseNumber !== undefined && { clause_number: nextClauseNumber }),
                  };
                })
              );

              const unmatched = entries
                .filter((e) => indices[e.id] === undefined)
                .map((e) => ({
                  clauseId: e.id,
                  searchSnippet: e.searchText.replace(/\s+/g, ' ').trim().slice(0, 120),
                }));

              debugLog('CrossCheckResultsView: paragraph sync completed', {
                matched: Object.keys(indices).length,
                totalEntries: entries.length,
                unmatchedCount: unmatched.length,
                unmatched,
                syncChanges,
                resolvedClauseNumbers: clauseNumbers,
              });
            })
            .catch((err) => {
              debugWarn('CrossCheckResultsView: paragraph sync failed', {
                message: (err as Error)?.message ?? String(err),
              });
              console.warn('[CrossCheckResultsView] Paragraph index sync failed:', err);
            })
            .finally(() => {
              syncInFlightRef.current = false;
              setSyncingIndices(false);
            });
        } else if (!syncInFlightRef.current) {
          setSyncingIndices(false);
          if (entries.length > 0) {
            debugLog('CrossCheckResultsView: paragraph sync skipped', {
              entryCount: entries.length,
              syncInFlight: syncInFlightRef.current,
              signatureUnchanged: syncSignature === lastSyncedSignatureRef.current,
              hasUnresolvedParagraphIndex,
            });
          }
        }
      } else {
        debugLog('CrossCheckResultsView: no data from server');
        setClauses([]);
        result = { ok: true, authError: false, progress: null };
      }
    } catch (err: any) {
      log('CrossCheckResultsView: load failed', { message: err?.message ?? String(err) });
      console.error('[CrossCheckResultsView] Error loading results:', err);
      const isAuthError = err?.isAuthError === true || err?.response?.status === 401;
      if (silent) {
        // Keep whatever is already on screen — wipe it only on a user-initiated load.
        // Show a subtle reconnecting indicator instead of an error banner.
        setPollingError(true);
      } else {
        setError(err.message || 'Failed to load cross-check results');
        setClauses([]);
      }
      result = { ok: false, authError: isAuthError };
    } finally {
      if (!silent) setLoading(false);
    }
    return result;
  }, [subProjectId, toRawClauseFromIdentifiedRequest, projectIdProp]);

  const handleToggleLock = React.useCallback(async (clause: CrossCheckClause) => {
    if (!subProjectId || !clause.id) return;
    setUpdatingLockId(clause.id);
    try {
      const response = await apiService.toggleClauseLock(subProjectId, clause.id);
      if (response?.success) {
        const newLocked = !clause.lock_status?.locked;
        setClauses((prev) =>
          prev.map((c) =>
            c.id === clause.id
              ? { ...c, lock_status: { ...(c.lock_status || {}), locked: newLocked } }
              : c
          )
        );
      }
    } catch (err: any) {
      console.error('[CrossCheckResultsView] Toggle lock failed:', err);
    } finally {
      setUpdatingLockId(null);
    }
  }, [subProjectId]);

  const handleClauseEdited = React.useCallback((updated: CrossCheckClause) => {
    setClauses((prev) => prev.map((c) => (c.id === updated.id ? updated : c)));
  }, []);

  const handleActionBanner = React.useCallback(
    (banner: { type: 'success' | 'error'; message: string }) => {
      setActionBanner(banner);
    },
    [],
  );

  const fetchBrainShareCounts = React.useCallback(async () => {
    if (!subProjectId) return;
    try {
      const response = await apiService.getUserBrainShares(subProjectId);
      if (response.success) {
        const entries = (response.data as Array<{ status?: string }>) || [];
        setBrainSharePendingCount(entries.filter((entry) => entry.status === 'in_process').length);
        setBrainShareTotalCount(entries.length);
      }
    } catch (err) {
      console.error('Error fetching BrainShare counts:', err);
    }
  }, [subProjectId]);

  React.useEffect(() => {
    void fetchBrainShareCounts();
  }, [fetchBrainShareCounts]);

  const handleBrainShareUpdated = React.useCallback(() => {
    setBrainShareTotalCount((prev) => prev + 1);
    setBrainSharePendingCount((prev) => prev + 1);
    void fetchBrainShareCounts();
  }, [fetchBrainShareCounts]);

  const handleResponseSubmitted = React.useCallback(
    (clauseId: string, revertContext?: SubmitRevertContext) => {
      setClauses((prev) =>
        prev.map((c) =>
          c.id === clauseId
            ? {
                ...c,
                submission_status: {
                  ...(c.submission_status || { schedule_submitted: false, response_submitted: false }),
                  response_submitted: true,
                  response_submitted_at: new Date().toISOString(),
                  response_revert_context: revertContext ?? null,
                },
              }
            : c
        )
      );
      if (revertContext) {
        setSubmitRevertContextByClauseId((prev) => ({ ...prev, [clauseId]: revertContext }));
      }
    },
    [],
  );

  const applyServerCrossChecksUpdate = React.useCallback((nextCrossChecks: RawCrossCheckClause[] | undefined | null) => {
    if (!Array.isArray(nextCrossChecks)) return;

    const activeRaw = nextCrossChecks.filter((c) => !c.stale);
    const activeIds = new Set(activeRaw.map((r) => r.id));
    const enableTableRowSupport = crossCheckSolutionRef.current === 'mcc';
    const mapped = activeRaw.map((rawClause) =>
      mapRawClause(rawClause, { enableTableRowSupport }),
    );

    setClauses((prev) => {
      const byId = new Map(prev.map((c) => [c.id, c]));
      mapped.forEach((updated) => {
        byId.set(updated.id, updated);
      });
      return Array.from(byId.values()).filter((c) => activeIds.has(c.id));
    });
  }, []);

  const extractApiErrorMessage = (err: unknown, fallback: string): string => {
    const anyErr = err as {
      response?: { data?: { message?: string; detail?: string | Array<{ msg?: string } | string> } };
      message?: string;
    };
    const detail = anyErr?.response?.data?.detail;
    if (typeof detail === 'string' && detail.trim()) return detail;
    if (Array.isArray(detail) && detail.length > 0) {
      return detail
        .map((d) => (typeof d === 'object' && d && 'msg' in d ? String(d.msg) : String(d)))
        .join('; ');
    }
    return anyErr?.response?.data?.message || anyErr?.message || fallback;
  };

  const handleMoveClause = React.useCallback(async (
    clause: CrossCheckClause,
    target: 'sales_agreement' | 'appendix_a' | 'saved_for_later',
  ) => {
    if (!subProjectId) return;
    if (clause.documentType === target) {
      setActionBanner({ type: 'success', message: 'Clause is already in that category.' });
      return;
    }

    const submissionStatus = clause.submission_status;
    if (submissionStatus?.schedule_submitted || submissionStatus?.response_submitted) {
      setActionBanner({
        type: 'error',
        message: 'This clause has already been submitted. Undo the submission before moving it.',
      });
      return;
    }

    const isRevertToOrigin =
      !!clause.origin_document_type && target === clause.origin_document_type;

    setMutatingClauseIds((prev) => ({ ...prev, [clause.id]: true }));
    setActionBanner({
      type: 'loading',
      message: isRevertToOrigin
        ? 'Restoring original category...'
        : target === 'saved_for_later'
          ? 'Moving to Saved...'
          : 'Moving and re-analyzing clause...',
    });

    try {
      const response = await apiService.moveClause(subProjectId, clause.id, target);
      if (!response?.success) {
        throw new Error(response?.message || 'Failed to move clause');
      }

      const updated = (response.data as { updated_cross_checks_results?: RawCrossCheckClause[] } | undefined)
        ?.updated_cross_checks_results;
      applyServerCrossChecksUpdate(updated);

      const targetLabel = target === 'sales_agreement'
        ? 'Sales Agreement'
        : target === 'appendix_a'
          ? 'Appendix A'
          : 'Saved';
      // MCC tabs are by documentType; Construction Loan tabs are by matter — skip tab jump on CL.
      if (detectedDomain !== 'construction-loan') {
        const tabKey: TabId = target === 'saved_for_later' ? 'saved' : target;
        setActiveTab(tabKey);
      }

      const reverted = (response.data as { reverted?: boolean } | undefined)?.reverted === true;
      setActionBanner({
        type: 'success',
        message: reverted
          ? `Clause restored to ${targetLabel}`
          : `Clause moved to ${targetLabel}`,
      });
    } catch (err) {
      console.error('[CrossCheckResultsView] Move clause failed:', err);
      setActionBanner({
        type: 'error',
        message: extractApiErrorMessage(err, 'Failed to move clause'),
      });
    } finally {
      setMutatingClauseIds((prev) => {
        const next = { ...prev };
        delete next[clause.id];
        return next;
      });
    }
  }, [subProjectId, applyServerCrossChecksUpdate, detectedDomain]);

  const handleMoveClauseMatter = React.useCallback(async (
    clause: CrossCheckClause,
    target: CrossCheckMatter,
  ) => {
    if (!subProjectId) return;
    const currentMatter = clause.matter || 'other';
    if (currentMatter === target) {
      setActionBanner({ type: 'success', message: 'Clause is already in that category.' });
      return;
    }

    const submissionStatus = clause.submission_status;
    if (submissionStatus?.schedule_submitted || submissionStatus?.response_submitted) {
      setActionBanner({
        type: 'error',
        message: 'This clause has already been submitted. Undo the submission before moving it.',
      });
      return;
    }

    const targetLabel =
      target === 'commercial' ? 'Commercial' : target === 'legal' ? 'Legal' : 'Other';

    setMutatingClauseIds((prev) => ({ ...prev, [clause.id]: true }));
    setActionBanner({ type: 'loading', message: `Moving to ${targetLabel}...` });

    try {
      const response = await apiService.moveClauseMatter(subProjectId, clause.id, target);
      if (!response?.success) {
        throw new Error(response?.message || 'Failed to move clause');
      }

      const updated = (response.data as { updated_cross_checks_results?: RawCrossCheckClause[] } | undefined)
        ?.updated_cross_checks_results;
      applyServerCrossChecksUpdate(updated);
      setActiveTab(target);
      setActionBanner({ type: 'success', message: `Clause moved to ${targetLabel}` });
    } catch (err) {
      console.error('[CrossCheckResultsView] Move clause matter failed:', err);
      setActionBanner({
        type: 'error',
        message: extractApiErrorMessage(err, 'Failed to move clause'),
      });
    } finally {
      setMutatingClauseIds((prev) => {
        const next = { ...prev };
        delete next[clause.id];
        return next;
      });
    }
  }, [subProjectId, applyServerCrossChecksUpdate]);

  const handleAnalyzeAgainOpen = React.useCallback((clause: CrossCheckClause) => {
    const isCl =
      detectedDomain === 'construction-loan' ||
      clause.matter === 'commercial' ||
      clause.matter === 'legal' ||
      clause.matter === 'other';
    setAnalyzeAgainVariant(isCl ? 'cl-past-negotiations' : 'mcc');
    setAnalyzeAgainClause(clause);
  }, [detectedDomain]);

  const handleAnalyzeAgainSubmit = React.useCallback(async (payload: {
    salesAgreementFeedback?: string;
    bankOfChangesFeedback?: string;
    pastNegotiationsFeedback?: string;
  }) => {
    if (!analyzeAgainClause || !subProjectId) {
      setActionBanner({ type: 'error', message: 'Missing context for re-analysis' });
      return;
    }

    const clause = analyzeAgainClause;
    const clauseId = clause.id;
    const clauseLabel = clause.clause_number ? `Clause ${formatClauseNumberForDisplay(clause.clause_number)}` : 'clause';

    // Close modal immediately so the user can keep working while re-analysis runs.
    setAnalyzeAgainClause(null);

    debugLog('CrossCheckResultsView: analyze again submit', {
      clauseId,
      variant: analyzeAgainVariant,
      subProjectId,
    });

    setMutatingClauseIds((prev) => ({ ...prev, [clauseId]: true }));
    const isClReanalyze = analyzeAgainVariant === 'cl-past-negotiations';
    setActionBanner({
      type: 'loading',
      message: isClReanalyze
        ? `Re-analyzing past negotiations for ${clauseLabel}...`
        : `Re-analyzing ${clauseLabel}...`,
    });

    try {
      const response = isClReanalyze
        ? await apiService.reanalyzePastNegotiations(
            subProjectId,
            clauseId,
            payload.pastNegotiationsFeedback || '',
          )
        : await apiService.reanalyzeClause(subProjectId, clauseId, {
            sales_agreement_feedback: payload.salesAgreementFeedback,
            bank_of_changes_feedback: payload.bankOfChangesFeedback,
          });
      if (!response?.success) {
        throw new Error(response?.message || 'Re-analysis failed');
      }

      const updated = (response.data as { updated_cross_checks_results?: RawCrossCheckClause[] } | undefined)
        ?.updated_cross_checks_results;
      applyServerCrossChecksUpdate(updated);
      setActionBanner({
        type: 'success',
        message: isClReanalyze
          ? `${clauseLabel} past negotiations re-analyzed`
          : `${clauseLabel} re-analyzed`,
      });
      debugLog('CrossCheckResultsView: analyze again success', { clauseId });
    } catch (err) {
      console.error('[CrossCheckResultsView] Reanalyze clause failed:', err);
      debugWarn('CrossCheckResultsView: analyze again failed', {
        clauseId,
        message: extractApiErrorMessage(err, 'Re-analysis failed'),
      });
      setActionBanner({
        type: 'error',
        message: extractApiErrorMessage(err, 'Re-analysis failed'),
      });
    } finally {
      setMutatingClauseIds((prev) => {
        const next = { ...prev };
        delete next[clauseId];
        return next;
      });
    }
  }, [analyzeAgainClause, analyzeAgainVariant, subProjectId, applyServerCrossChecksUpdate]);

  React.useEffect(() => {
    if (!actionBanner || actionBanner.type === 'loading') return;
    const timer = window.setTimeout(() => setActionBanner(null), 5000);
    return () => window.clearTimeout(timer);
  }, [actionBanner]);

  const handleResponseUndone = React.useCallback((clauseId: string) => {
    setClauses((prev) =>
      prev.map((c) =>
        c.id === clauseId
          ? {
              ...c,
              submission_status: {
                ...(c.submission_status || { schedule_submitted: false, response_submitted: false }),
                response_submitted: false,
                response_submitted_at: undefined,
                response_revert_context: undefined,
              },
            }
          : c
      )
    );
    setSubmitRevertContextByClauseId((prev) => {
      const next = { ...prev };
      delete next[clauseId];
      return next;
    });
  }, []);

  /** Only changes when past_agreement payloads change — not on paragraph_index / sync-only updates. */
  const pastAgreementDataFingerprint = React.useMemo(
    () => clauses.map((c) => JSON.stringify((c as any).past_agreement ?? null)).join('\n'),
    [clauses]
  );

  const [playbookXmlById, setPlaybookXmlById] = React.useState<Record<string, string>>({});
  const [playbookSideById, setPlaybookSideById] = React.useState<
    Record<string, 'our_side' | 'their_side'>
  >({});
  const [playbookFileNameById, setPlaybookFileNameById] = React.useState<Record<string, string>>({});
  const [playbookFileIdById, setPlaybookFileIdById] = React.useState<Record<string, string>>({});
  const [playbookFinalVersionById, setPlaybookFinalVersionById] = React.useState<Record<string, boolean>>({});
  const [playbookPlainTextById, setPlaybookPlainTextById] = React.useState<Record<string, string>>({});
  const [playbookAuthorById, setPlaybookAuthorById] = React.useState<Record<string, string>>({});
  const [playbookTimestampById, setPlaybookTimestampById] = React.useState<Record<string, string>>({});
  const [playbooksBatchLoading, setPlaybooksBatchLoading] = React.useState(false);

  const allPlaybookIdsForBatch = React.useMemo(
    () => collectAllPastAgreementPlaybookIds(clauses),
    [pastAgreementDataFingerprint]
  );

  React.useEffect(() => {
    const resolvedProjectId = projectId ?? projectIdProp;
    if (!resolvedProjectId) {
      setPlaybookXmlById({});
      setPlaybookSideById({});
      setPlaybookFileNameById({});
      setPlaybookFileIdById({});
      setPlaybookFinalVersionById({});
      setPlaybookPlainTextById({});
      setPlaybookAuthorById({});
      setPlaybookTimestampById({});
      setPlaybooksBatchLoading(false);
      return;
    }
    const ids = allPlaybookIdsForBatch;
    if (ids.length === 0) {
      setPlaybookXmlById({});
      setPlaybookSideById({});
      setPlaybookFileNameById({});
      setPlaybookFileIdById({});
      setPlaybookFinalVersionById({});
      setPlaybookPlainTextById({});
      setPlaybookAuthorById({});
      setPlaybookTimestampById({});
      setPlaybooksBatchLoading(false);
      return;
    }
    if (detectedDomain === null) {
      setPlaybooksBatchLoading(true);
      return;
    }

    let cancelled = false;
    setPlaybooksBatchLoading(true);
    void (async () => {
      try {
        const response = await apiService.getPastContractsPlaybooksByIds(
          resolvedProjectId,
          ids,
          detectedDomain ?? 'construction-loan',
        );
        if (cancelled) return;
        if (!response.success || !Array.isArray(response.data)) {
          console.warn('CrossCheckResultsView: past agreement playbooks response invalid');
          if (!cancelled) {
            setPlaybookXmlById({});
            setPlaybookSideById({});
            setPlaybookFileNameById({});
            setPlaybookFileIdById({});
            setPlaybookFinalVersionById({});
            setPlaybookPlainTextById({});
            setPlaybookAuthorById({});
            setPlaybookTimestampById({});
          }
          return;
        }
        const rows = response.data as Array<{
          id?: string;
          paragraph_xml?: string;
          paragraph_text?: string;
          side?: string;
          source_filename?: string;
          source_doc_type?: string;
          file_id?: string;
          author?: string;
          timestamp?: string;
        }>;
        const byId: Record<string, string> = {};
        const sideById: Record<string, 'our_side' | 'their_side'> = {};
        const fileNameById: Record<string, string> = {};
        const fileIdById: Record<string, string> = {};
        const finalVersionById: Record<string, boolean> = {};
        const plainTextById: Record<string, string> = {};
        const authorById: Record<string, string> = {};
        const timestampById: Record<string, string> = {};
        for (const row of rows) {
          if (row?.id && row?.paragraph_xml) {
            byId[row.id] = row.paragraph_xml;
          }
          if (row?.id && (row?.side === 'our_side' || row?.side === 'their_side')) {
            sideById[row.id] = row.side;
          }
          if (row?.id) {
            if (row.source_filename?.trim()) {
              fileNameById[row.id] = row.source_filename.trim();
            }
            if (row.file_id?.trim()) {
              fileIdById[row.id] = row.file_id.trim();
            }
            if (row.source_doc_type === 'final_version') {
              finalVersionById[row.id] = true;
            }
            if (row.paragraph_text?.trim()) {
              plainTextById[row.id] = row.paragraph_text.trim();
            }
            if (row.author?.trim()) {
              authorById[row.id] = row.author.trim();
            }
            if (row.timestamp?.trim()) {
              timestampById[row.id] = row.timestamp.trim();
            }
          }
        }
        debugLog('CrossCheckResultsView: past agreement playbooks loaded', {
          requestedIds: ids.length,
          rows: rows.length,
        });
        setPlaybookXmlById(byId);
        setPlaybookSideById(sideById);
        setPlaybookFileNameById(fileNameById);
        setPlaybookFileIdById(fileIdById);
        setPlaybookFinalVersionById(finalVersionById);
        setPlaybookPlainTextById(plainTextById);
        setPlaybookAuthorById(authorById);
        setPlaybookTimestampById(timestampById);
      } catch (e) {
        debugWarn('CrossCheckResultsView: past agreement playbooks failed', {
          message: e instanceof Error ? e.message : String(e),
        });
        if (!cancelled) {
          setPlaybookXmlById({});
          setPlaybookSideById({});
          setPlaybookFileNameById({});
          setPlaybookFileIdById({});
          setPlaybookFinalVersionById({});
          setPlaybookPlainTextById({});
          setPlaybookAuthorById({});
          setPlaybookTimestampById({});
        }
      } finally {
        if (!cancelled) setPlaybooksBatchLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [projectId, projectIdProp, allPlaybookIdsForBatch, detectedDomain]);

  // ─── Sales Agreement "Go to" split-view state ─────────────────────────────
  // When the user clicks the "Go to" badge on a clause's Sales Agreement section,
  // we fetch the SA source PDF (highlighted on the matched page) and show it in a
  // split view: PDF on the left, clause list on the right (TaskPane portaled dock).
  type SalesAgreementSourceView = {
    clauseId: string;
    clauseNumberDisplay: string | null;
    loading: boolean;
    error: string | null;
    buffer: ArrayBuffer | null;
    page: number;
    total: number;
    /** Original clause text — drives the panel's client-side fuzzy highlight + scroll. */
    searchText: string;
    /** Same clause_number the dashboard sends on card-header click. */
    clauseNumber?: string;
  };
  const [salesAgreementSourceView, setSalesAgreementSourceView] =
    React.useState<SalesAgreementSourceView | null>(null);

  type PlaybookAgentBesideView = {
    sessionKey: string;
    clauseId: string;
    clauseNumberDisplay: string | null;
    loading: boolean;
    error: string | null;
    contextBody: string;
    projectLabel: string;
    recordCount: number;
    playbookRows: PlaybookArchiveRow[];
    matchRankToArchiveRecordNum: Map<number, number>;
    playbookBodyCached: string;
    askDomain: AskAgentDomain;
    askLanguage: SupportedLanguage;
    initialQuestion: string | null;
    openingAssistantMessage: string | null;
    awaitingDeepResearchConsent: boolean;
    awaitingContextSourceChoice: boolean;
    contextSource: AskAgentContextSource | null;
    /** Persisted auto-apply conversation replay (read-only). */
    readOnly?: boolean;
    initialMessages?: PlaybookAgentChatTurn[];
  };
  const [playbookAgentBeside, setPlaybookAgentBeside] =
    React.useState<PlaybookAgentBesideView | null>(null);
  const playbookAgentLoadRef = React.useRef(0);
  const [agentAiStreamingClauseId, setAgentAiStreamingClauseId] = React.useState<string | null>(
    null,
  );
  const [agentSuggestedXmlByClauseId, setAgentSuggestedXmlByClauseId] = React.useState<
    Record<string, string>
  >({});

  /** Sub-project review mode from metadata (defaults to redlines). */
  const [reviewMode, setReviewMode] = React.useState<ReviewMode>('review_and_respond_redlines');
  const [agentAutoApplyEnabled, setAgentAutoApplyEnabled] = React.useState(false);
  const agentAutoApplyStartedForSubProjectRef = React.useRef<string | null>(null);
  const agentAutoApplyAbortRef = React.useRef(false);
  /** Per-phase progress (Ask Agent / suggested edits / submit) for the extra step rows. */
  const [agentPhaseProgress, setAgentPhaseProgress] = React.useState<AgentPhaseProgress>(
    INITIAL_AGENT_PHASE_PROGRESS,
  );
  const agentAutoApplyRunningRef = React.useRef(false);
  /** The run under way; `hidden` once the view was hidden during it, which stops it at its next step. */
  const agentAutoApplyRunRef = React.useRef<{ hidden: boolean } | null>(null);
  /**
   * Cross-check is done and the agent pipeline is wanted, but it has not
   * reported anything yet.
   *
   * The three agent step rows are driven entirely by pipeline progress events,
   * and the first of those only fires once the first Ask batch is in flight.
   * Everything before it — the full sub-project reload the poll triggers, the
   * playbook batch it invalidates, then a second round of both — is invisible:
   * the rows sit at "pending" (circle), which is exactly how they look when
   * nothing is happening at all. Measured on a 27-clause run: 124 seconds
   * between cross-check finishing and the first Ask, with nothing on screen
   * saying the stage had begun.
   */
  const [agentAutoApplyArmed, setAgentAutoApplyArmed] = React.useState(false);
  /**
   * Re-opens the auto-apply gate on its own clock while the stage is armed.
   *
   * The gate below is an effect, so it only re-evaluates when one of its
   * dependencies changes — and those change when a poll tick lands. That made
   * starting the agent depend on a future poll even though the answer was
   * already in hand: measured three times, cross-check finished and the first
   * Ask went out 124.0s, 124.6s and 125.2s later. The poll was losing that
   * time to the multi-megabyte reload the same completion triggered — one tick
   * died on the 30s request timeout, the next took 22s — so the gate sat shut
   * waiting for a tick that was starving behind its own payload.
   *
   * Re-checking on a timer makes the start depend on the gate actually being
   * open rather than on something happening to re-render. It does not widen
   * the gate: every condition below still has to hold.
   */
  const [agentGateTick, setAgentGateTick] = React.useState(0);
  /** When the stage was armed, so the gate's own wait is reported, not inferred. */
  const agentArmedAtRef = React.useRef<number | null>(null);
  React.useEffect(() => {
    if (!agentAutoApplyArmed) return undefined;
    let attempts = 0;
    const id = window.setInterval(() => {
      attempts += 1;
      // Armed is cleared when the run starts or when there turns out to be no
      // work, so this normally stops long before the cap; the cap only keeps a
      // stuck gate from leaving a timer running for the session.
      if (attempts > AGENT_GATE_RETRY_MAX_ATTEMPTS) {
        window.clearInterval(id);
        return;
      }
      setAgentGateTick((n) => n + 1);
    }, AGENT_GATE_RETRY_MS);
    return () => window.clearInterval(id);
  }, [agentAutoApplyArmed]);
  /**
   * Set when a run stopped mid-phase — a usage-limit hit, or the session
   * expiring (401) — distinct from a normal completion or a genuine
   * per-clause failure. The clauses it didn't get to are left untouched by
   * the pipeline (see runAgentAutoApplyPipeline's checkForPause), so
   * clicking "Resume" once the cause is fixed picks up exactly where it
   * stopped instead of skipping them as phantom errors.
   */
  const [agentPause, setAgentPause] = React.useState<PipelinePauseInfo | null>(null);
  /** Bumped by the "Resume" action to force the auto-apply effect to re-evaluate. */
  const [agentResumeTick, setAgentResumeTick] = React.useState(0);
  /** Which agent step row (if any) is expanded to show its per-clause error rows. */
  const [expandedAgentPhase, setExpandedAgentPhase] = React.useState<AgentPhaseKey | null>(null);
  /** Clauses whose suggested edit was generated but never applied to Word — derived
   *  from persisted data, so it's correct immediately after a reload too. */
  const agentSubmitFailures = React.useMemo(() => getAgentSubmitFailures(clauses), [clauses]);

  /** Tracks the in-flight target clause id so a slow response from a previous clause
   *  doesn't overwrite the panel after the user clicked a different clause. */
  const salesAgreementSourceTargetRef = React.useRef<string | null>(null);

  /** PDF column width (%); persisted; adjusted via drag handle. */
  const [saBesidePdfWidthPct, setSaBesidePdfWidthPct] = React.useState(readStoredBesidePdfPct);
  const saBesidePdfWidthPctRef = React.useRef(saBesidePdfWidthPct);
  React.useEffect(() => {
    saBesidePdfWidthPctRef.current = saBesidePdfWidthPct;
  }, [saBesidePdfWidthPct]);

  const resultsViewRef = React.useRef<HTMLDivElement>(null);
  const besideResizeDragRef = React.useRef<{
    pointerId: number;
    startX: number;
    startPct: number;
    containerWidth: number;
  } | null>(null);

  const persistBesidePdfPct = React.useCallback((pct: number) => {
    const clamped = Math.round(
      Math.min(MAX_BESIDE_PDF_PCT, Math.max(MIN_BESIDE_PDF_PCT, pct)),
    );
    try {
      localStorage.setItem(SA_BESIDE_SPLIT_PCT_KEY, String(clamped));
    } catch {
      /* ignore */
    }
    return clamped;
  }, []);

  const handleBesideResizePointerDown = React.useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (e.button !== 0) return;
      const root = besideResizeOuterRef?.current ?? resultsViewRef.current;
      if (!root) return;
      const rect = root.getBoundingClientRect();
      besideResizeDragRef.current = {
        pointerId: e.pointerId,
        startX: e.clientX,
        startPct: saBesidePdfWidthPctRef.current,
        containerWidth: Math.max(1, rect.width),
      };
      e.currentTarget.setPointerCapture(e.pointerId);
      e.preventDefault();
    },
    [besideResizeOuterRef],
  );

  const handleBesideResizePointerMove = React.useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    const d = besideResizeDragRef.current;
    if (!d || e.pointerId !== d.pointerId) return;
    const delta = e.clientX - d.startX;
    const deltaPct = (delta / d.containerWidth) * 100;
    const next = Math.min(
      MAX_BESIDE_PDF_PCT,
      Math.max(MIN_BESIDE_PDF_PCT, d.startPct + deltaPct),
    );
    setSaBesidePdfWidthPct(Math.round(next * 10) / 10);
  }, []);

  const scheduleScrollActiveBesideClauseRef = React.useRef<() => void>(() => {});
  const prevBesidePanelOpenRef = React.useRef(false);
  /** Clause to re-align under sticky tabs after the beside panel closes (X / toggle-off only). */
  const pendingCloseClauseScrollRef = React.useRef<string | null>(null);

  const endBesideResizeDrag = React.useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    const d = besideResizeDragRef.current;
    if (!d || e.pointerId !== d.pointerId) return;
    besideResizeDragRef.current = null;
    try {
      e.currentTarget.releasePointerCapture(e.pointerId);
    } catch {
      /* already released */
    }
    setSaBesidePdfWidthPct((prev) => persistBesidePdfPct(prev));
    requestAnimationFrame(() => scheduleScrollActiveBesideClauseRef.current());
  }, [persistBesidePdfPct]);

  const handleBesideResizeKeyDown = React.useCallback((e: React.KeyboardEvent<HTMLDivElement>) => {
    let delta = 0;
    if (e.key === 'ArrowLeft') delta = -2;
    else if (e.key === 'ArrowRight') delta = 2;
    else if (e.key === 'Home') {
      e.preventDefault();
      setSaBesidePdfWidthPct(persistBesidePdfPct(MIN_BESIDE_PDF_PCT));
      requestAnimationFrame(() => scheduleScrollActiveBesideClauseRef.current());
      return;
    } else if (e.key === 'End') {
      e.preventDefault();
      setSaBesidePdfWidthPct(persistBesidePdfPct(MAX_BESIDE_PDF_PCT));
      requestAnimationFrame(() => scheduleScrollActiveBesideClauseRef.current());
      return;
    } else return;
    e.preventDefault();
    setSaBesidePdfWidthPct((prev) => persistBesidePdfPct(prev + delta));
    requestAnimationFrame(() => scheduleScrollActiveBesideClauseRef.current());
  }, [persistBesidePdfPct]);

  const handleBesideResizeDoubleClick = React.useCallback(() => {
    setSaBesidePdfWidthPct(persistBesidePdfPct(DEFAULT_BESIDE_PDF_PCT));
    requestAnimationFrame(() => scheduleScrollActiveBesideClauseRef.current());
  }, [persistBesidePdfPct]);

  const mccTaskPaneBesideWire =
    !!pdfBesideDockRef && !!besideHandleSlotRef && !!onMccBesidePdfLayoutChange;

  const besidePanelOpen = !!salesAgreementSourceView || !!playbookAgentBeside;

  React.useLayoutEffect(() => {
    if (!mccTaskPaneBesideWire) return;
    const docked = besidePanelOpen;
    onMccBesidePdfLayoutChange!({ docked, pdfWidthPct: saBesidePdfWidthPct });
  }, [mccTaskPaneBesideWire, onMccBesidePdfLayoutChange, besidePanelOpen, saBesidePdfWidthPct]);

  React.useLayoutEffect(() => {
    if (!mccTaskPaneBesideWire) return;
    const cb = onMccBesidePdfLayoutChange!;
    return () => {
      cb({ docked: false, pdfWidthPct: saBesidePdfWidthPctRef.current });
    };
  }, [mccTaskPaneBesideWire, onMccBesidePdfLayoutChange]);

  React.useLayoutEffect(() => {
    if (workflowScrollRequestToken <= consumedWorkflowScrollTokenRef.current) return;
    consumedWorkflowScrollTokenRef.current = workflowScrollRequestToken;
    workflowStepsScrollAnchorRef.current?.scrollIntoView({ block: 'start', behavior: 'auto' });
  }, [workflowScrollRequestToken]);

  const stickyHighlightClearTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const [stickyHighlightedClauseId, setStickyHighlightedClauseId] = React.useState<string | null>(
    null,
  );

  /** Align a clause card header just under the sticky progress/tabs row (open or closed beside layout). */
  const scrollFocusedClauseHeaderIntoView = React.useCallback(
    (clauseId: string) => {
      const headerEl = clauseCardHeaderRefs.current.get(clauseId);
      if (!headerEl) return false;

      const scrollRoot = besidePanelOpen
        ? (besideRightScrollRef?.current ??
          resultsMainColumnRef.current ??
          findScrollableAncestor(headerEl))
        : (findScrollableAncestor(headerEl) ?? resultsMainColumnRef.current);
      if (!scrollRoot) return false;

      const stickyEl = stickyHeaderRef.current;
      const anchorBottom = stickyEl
        ? stickyEl.getBoundingClientRect().bottom
        : scrollRoot.getBoundingClientRect().top;
      const headerTop = headerEl.getBoundingClientRect().top;
      const delta = headerTop - anchorBottom;
      if (Math.abs(delta) < 1) return true;

      scrollRoot.scrollTop = scrollRoot.scrollTop + delta;
      return true;
    },
    [besideRightScrollRef, besidePanelOpen],
  );

  const scheduleScrollClauseHeaderIntoView = React.useCallback(
    (clauseId: string) => {
      const run = () => scrollFocusedClauseHeaderIntoView(clauseId);
      run();
      requestAnimationFrame(() => {
        run();
        requestAnimationFrame(run);
      });
    },
    [scrollFocusedClauseHeaderIntoView],
  );

  const scheduleScrollActiveBesideClauseIntoView = React.useCallback(() => {
    const clauseId =
      playbookAgentBeside?.clauseId ?? salesAgreementSourceView?.clauseId ?? null;
    if (!clauseId || !besidePanelOpen) return;
    scheduleScrollClauseHeaderIntoView(clauseId);
  }, [
    playbookAgentBeside?.clauseId,
    salesAgreementSourceView?.clauseId,
    besidePanelOpen,
    scheduleScrollClauseHeaderIntoView,
  ]);

  React.useEffect(() => {
    scheduleScrollActiveBesideClauseRef.current = scheduleScrollActiveBesideClauseIntoView;
  }, [scheduleScrollActiveBesideClauseIntoView]);

  const [stickyScrollEnabled, setStickyScrollEnabled] = React.useState(isStickyScrollToCardEnabled);
  React.useEffect(
    () =>
      subscribeStickyScrollToCardEnabled(() => {
        setStickyScrollEnabled(isStickyScrollToCardEnabled());
      }),
    [],
  );

  /**
   * Latest beside-panel state and opener, read by the sticky selection
   * handler. Held in refs so following the selection does not re-subscribe
   * the Word selection tracker every time the panel changes.
   */
  const playbookAgentBesideRef = React.useRef(playbookAgentBeside);
  playbookAgentBesideRef.current = playbookAgentBeside;
  const openPlaybookAgentRef = React.useRef<
    ((clause: CrossCheckClause) => Promise<void>) | null
  >(null);
  const openAgentAutoResultRef = React.useRef<((clause: CrossCheckClause) => void) | null>(null);
  /**
   * The project's playbook archive, fetched once per pane session.
   *
   * Opening Ask Agent re-fetched the whole archive every time, which was
   * tolerable while opening was a deliberate click. Now that the panel follows
   * the document selection it would refetch on every click in the document —
   * into the same fetch the connection pool starves for tens of seconds. The
   * archive does not change while the pane is open.
   */
  const playbookArchiveCacheRef = React.useRef<{
    key: string;
    rows: PlaybookArchiveRow[];
  } | null>(null);

  const loadPlaybookArchive = React.useCallback(
    async (
      resolvedProjectId: string,
      workflowHint: 'construction_loan' | 'real_estate',
    ): Promise<PlaybookArchiveRow[]> => {
      const key = `${resolvedProjectId}|${workflowHint}`;
      const cached = playbookArchiveCacheRef.current;
      if (cached?.key === key) return cached.rows;
      const fetched = await apiService.listPlaybooksForAgentDetailed(
        resolvedProjectId,
        workflowHint,
      );
      // Never cache an archive we failed to reach as if it were empty — that
      // is the mistake the auto-apply pipeline made, and it would be stickier
      // here because the cache outlives the failure.
      if (!fetched.allAttemptsFailed) {
        playbookArchiveCacheRef.current = { key, rows: fetched.rows };
      }
      return fetched.rows;
    },
    [],
  );

  const lastStickyParagraphKeyRef = React.useRef<string | null>(null);
  const pendingStickyScrollClauseIdRef = React.useRef<string | null>(null);
  const displayedClausesRef = React.useRef(0);
  const stickyFilterStateRef = React.useRef<{
    hasActiveClauseSubFilters: boolean;
    displayedCount: number;
  }>({ hasActiveClauseSubFilters: false, displayedCount: 0 });
  const stickyTrackerRef = React.useRef<SelectionTracker | null>(null);

  /**
   * Which card the list shows under the sticky header now, measured — not
   * assumed from the chosen card. Logged after a Sticky scroll so a report
   * says where the list ended up, not only which card was chosen.
   */
  const measureStickyLanding = React.useCallback(
    (clauseId: string, scrolledAt: number): StickyLanding => {
      const headerEl = clauseCardHeaderRefs.current.get(clauseId) ?? null;
      const scrollRoot = headerEl
        ? besidePanelOpen
          ? (besideRightScrollRef?.current ?? resultsMainColumnRef.current ?? findScrollableAncestor(headerEl))
          : (findScrollableAncestor(headerEl) ?? resultsMainColumnRef.current)
        : resultsMainColumnRef.current;
      const stickyEl = stickyHeaderRef.current;
      const anchorBottom = stickyEl
        ? stickyEl.getBoundingClientRect().bottom
        : (scrollRoot?.getBoundingClientRect().top ?? 0);
      const line = anchorBottom + 4;
      const cards = Array.from(
        (scrollRoot ?? document).querySelectorAll<HTMLElement>('[data-testid="cc-clause-card"]'),
      );
      const top = cards.find((card) => {
        const rect = card.getBoundingClientRect();
        return rect.top <= line && rect.bottom > line;
      }) ?? cards.find((card) => card.getBoundingClientRect().top >= anchorBottom) ?? null;
      const target = cards.find((card) => card.getAttribute('data-clause-id') === clauseId) ?? null;
      const maxScrollTop = scrollRoot ? scrollRoot.scrollHeight - scrollRoot.clientHeight : null;
      return {
        at: Date.now(),
        msAfterScroll: Date.now() - scrolledAt,
        targetClauseId: clauseId,
        targetShownNumber: target?.getAttribute('data-clause-number') ?? null,
        topClauseId: top?.getAttribute('data-clause-id') ?? null,
        topShownNumber: top?.getAttribute('data-clause-number') ?? null,
        targetOffsetPx: headerEl ? Math.round(headerEl.getBoundingClientRect().top - anchorBottom) : null,
        atScrollEnd:
          scrollRoot && maxScrollTop != null ? scrollRoot.scrollTop >= maxScrollTop - 2 : null,
        scrollRoot: scrollRoot ? (scrollRoot.className || scrollRoot.tagName).toString().slice(0, 60) : null,
        scrollTop: scrollRoot ? Math.round(scrollRoot.scrollTop) : null,
        maxScrollTop: maxScrollTop != null ? Math.round(maxScrollTop) : null,
        viewport: `${window.innerWidth}x${window.innerHeight}@${window.devicePixelRatio}`,
        besidePanelOpen: Boolean(besidePanelOpen),
      };
    },
    [besidePanelOpen, besideRightScrollRef],
  );
  const stickyLandingTimersRef = React.useRef<Array<ReturnType<typeof setTimeout>>>([]);
  const stickyAlignTimersRef = React.useRef<Array<ReturnType<typeof setTimeout>>>([]);

  /**
   * Bring a card under the sticky header for Sticky: an instant scroll, then
   * the position checked and corrected twice more.
   *
   * It was a smooth scroll with one correction after 420ms. In Word on
   * Windows (Chromium) that animation stopped 1,100-1,300px short of a card a
   * long way off and stayed there — a user's debug log measured it on 8 of 11
   * clicks — so the right card was chosen and a different one shown. Word on
   * Mac finished it. With no animation there is nothing for the engine to cut
   * short.
   */
  const scrollStickyClauseIntoView = React.useCallback(
    (clauseId: string) => {
      scheduleScrollClauseHeaderIntoView(clauseId);
      stickyAlignTimersRef.current.forEach((timer) => clearTimeout(timer));
      stickyAlignTimersRef.current = [300, 800].map((ms) =>
        setTimeout(() => scrollFocusedClauseHeaderIntoView(clauseId), ms),
      );
    },
    [scheduleScrollClauseHeaderIntoView, scrollFocusedClauseHeaderIntoView],
  );

  const revealAndScrollStickyClause = React.useCallback(
    (clauseId: string) => {
      // Prefer All so tab filters do not hide the target card.
      setActiveTab((prev) => (prev === 'all' ? prev : 'all'));
      pendingStickyScrollClauseIdRef.current = clauseId;

      if (stickyHighlightClearTimerRef.current != null) {
        clearTimeout(stickyHighlightClearTimerRef.current);
      }
      setStickyHighlightedClauseId(clauseId);
      stickyHighlightClearTimerRef.current = setTimeout(() => {
        stickyHighlightClearTimerRef.current = null;
        setStickyHighlightedClauseId((prev) => (prev === clauseId ? null : prev));
      }, 1400);

      if (clauseCardHeaderRefs.current.has(clauseId)) {
        scrollStickyClauseIntoView(clauseId);
        pendingStickyScrollClauseIdRef.current = null;
      }

      // Measure where the list ended up: once after the smooth scroll and its
      // settle nudge, and once more later in case the layout kept moving.
      const scrolledAt = Date.now();
      stickyLandingTimersRef.current.forEach((timer) => clearTimeout(timer));
      stickyLandingTimersRef.current = [1000, 2500].map((ms) =>
        setTimeout(() => {
          const landing = measureStickyLanding(clauseId, scrolledAt);
          recordStickyLanding(landing);
          log('[Sticky] landed', {
            ok: landing.topClauseId === clauseId,
            ...landing,
          });
        }, ms),
      );
    },
    [scrollStickyClauseIntoView, measureStickyLanding],
  );

  React.useEffect(() => {
    return () => {
      if (stickyHighlightClearTimerRef.current != null) {
        clearTimeout(stickyHighlightClearTimerRef.current);
        stickyHighlightClearTimerRef.current = null;
      }
      stickyLandingTimersRef.current.forEach((timer) => clearTimeout(timer));
      stickyAlignTimersRef.current.forEach((timer) => clearTimeout(timer));
    };
  }, []);

  React.useEffect(() => {
    // Hidden behind another tab, a selection made in Word is not this view's: following it would scroll
    // the hidden list and open Ask Agent sessions nobody sees.
    if (!stickyScrollEnabled || !active) {
      lastStickyParagraphKeyRef.current = null;
      stickyTrackerRef.current?.stopTracking();
      return;
    }
    if (typeof Word === 'undefined' || typeof Word.run !== 'function') {
      return;
    }

    const tracker = stickyTrackerRef.current ?? new SelectionTracker({ debounceMs: 180 });
    stickyTrackerRef.current = tracker;

    const unsubscribe = tracker.onSelectionChange(() => {
      void (async () => {
        if (!isStickyScrollToCardEnabled()) return;
        // Every branch below says which way it went in the log: "sticky
        // sometimes doesn't scroll" was unreportable while they returned
        // silently.
        //
        // The card is chosen by the paragraph's TEXT, the way a card's go-to
        // finds its paragraph. The stored paragraph index comes from the
        // analysis and goes stale as soon as the document changes — a
        // paragraph added above, a response applied — and a stale index
        // points at the neighbouring card.
        // The client's own revisions are the requests; ours are the responses.
        const clientAuthors = new Set(
          clausesRef.current.flatMap((c) => (Array.isArray(c.participants) ? c.participants : [])),
        );
        const cursor = await DocumentReader.readCursorParagraphForSticky(clientAuthors);
        const cursorKey = cursor?.shownNorm || cursor?.acceptedNorm || '';
        if (!cursor || !cursorKey) {
          log('[Sticky] cursor paragraph has no text');
          // Coming back from a line with no text to the same paragraph is a new visit.
          lastStickyParagraphKeyRef.current = null;
          recordStickyDecision({ paragraphIndex: null, clauseId: null, clauseNumber: null, matchedBy: 'no-text' });
          return;
        }
        if (lastStickyParagraphKeyRef.current === cursorKey) return;
        lastStickyParagraphKeyRef.current = cursorKey;
        const idx = cursor.index;

        const list = clausesRef.current;
        const ctx = { solution: crossCheckSolutionRef.current };
        const candidates = list.map((c) => ({
          id: c.id,
          index: resolveEffectiveParagraphIndex(c),
          searchNorms: stickySearchTextsForClause(c, ctx).map((t) =>
            DocumentReader.normalizeTextForMatch(t),
          ),
        }));
        let matchedBy: 'text' | 'paragraph-index' | 'none' = 'none';
        let score: number | null = null;
        let match: CrossCheckClause | null = null;
        const picked = pickStickyCardByText(
          [cursor.clientNorm, cursor.shownNorm, cursor.acceptedNorm],
          candidates,
          idx,
        );
        if (picked) {
          match = list.find((c) => c.id === picked.id) ?? null;
          matchedBy = 'text';
          score = picked.score;
        } else if (idx != null) {
          // The index alone is trusted only for a card that has no text to
          // check it against; for any other card a text miss means the index
          // is not this paragraph's.
          const unverifiable = candidates.find(
            (c) => c.index === idx && !c.searchNorms.some((s) => s.length >= MIN_CLAUSE_SEARCH_LEN),
          );
          if (unverifiable) {
            match = list.find((c) => c.id === unverifiable.id) ?? null;
            matchedBy = 'paragraph-index';
          }
        }

        recordStickyDecision({
          paragraphIndex: idx,
          clauseId: match?.id ?? null,
          clauseNumber: match?.clause_number ?? null,
          matchedBy,
        });
        if (!match?.id) {
          log('[Sticky] no card matches this paragraph', {
            paragraphIndex: idx,
            head: cursorKey.slice(0, 60),
            clauseCount: list.length,
          });
          return;
        }
        debugLog('CrossCheckResultsView: sticky scroll to card', {
          clauseId: match.id,
          paragraphIndex: idx,
        });
        log('[Sticky] scrolling to card', {
          paragraphIndex: idx,
          matchedBy,
          score,
          clauseNumber: match.clause_number ?? null,
          // A card hidden by the active tab cannot be scrolled to until the
          // switch to "all" has rendered — a known way for this to look flaky.
          headerMounted: clauseCardHeaderRefs.current.has(match.id),
          activeTab,
          ...stickyFilterStateRef.current,
        });
        revealAndScrollStickyClause(match.id);

        // Ask Agent is pinned to one clause. Scrolling the list to a different
        // clause while the panel still answers for the previous one leaves two
        // halves of the screen disagreeing, with nothing saying so — so the
        // panel follows the selection too.
        //
        // Show what the agent already concluded for the new clause when auto
        // apply has run on it. Opening a fresh session there would throw that
        // answer away and ask the lawyer to start over on a card that is
        // already decided.
        const openAgentClauseId = playbookAgentBesideRef.current?.clauseId;
        if (openAgentClauseId && openAgentClauseId !== match.id) {
          if (hasSavedAgentAutoResult(match)) {
            openAgentAutoResultRef.current?.(match);
          } else {
            void openPlaybookAgentRef.current?.(match);
          }
        }
      })();
    });

    tracker.startTracking();
    return () => {
      unsubscribe();
      tracker.stopTracking();
    };
  }, [stickyScrollEnabled, revealAndScrollStickyClause, active]);

  type BesideCloseOptions = { alignClause?: boolean };

  const handleCloseSalesAgreementSource = React.useCallback(
    (opts?: BesideCloseOptions) => {
      if (opts?.alignClause) {
        const clauseId = salesAgreementSourceView?.clauseId;
        if (clauseId) pendingCloseClauseScrollRef.current = clauseId;
      }
      salesAgreementSourceTargetRef.current = null;
      setSalesAgreementSourceView(null);
    },
    [salesAgreementSourceView?.clauseId],
  );

  const handleClosePlaybookAgent = React.useCallback(
    (opts?: BesideCloseOptions) => {
      if (opts?.alignClause) {
        const clauseId = playbookAgentBeside?.clauseId;
        if (clauseId) pendingCloseClauseScrollRef.current = clauseId;
      }
      playbookAgentLoadRef.current += 1;
      setPlaybookAgentBeside(null);
    },
    [playbookAgentBeside?.clauseId],
  );

  const handleOpenPlaybookAgent = React.useCallback(
    async (clause: CrossCheckClause) => {
      const resolvedProjectId = projectId ?? projectIdProp;
      if (!resolvedProjectId) {
        setActionBanner({
          type: 'error',
          message: 'Project is not linked — cannot load playbook for Ask Agent.',
        });
        return;
      }

      handleCloseSalesAgreementSource();

      const clauseNumberDisplay =
        clause.clause_number != null
          ? formatClauseNumberForDisplay(clause.clause_number)
          : null;
      const askDomain: AskAgentDomain =
        detectedDomain === 'construction-loan' ? 'construction-loan' : 'real-estate';
      const projectLabel =
        parentProjectName ||
        (askDomain === 'construction-loan' ? 'Past negotiations' : 'Negotiation archive');
      const workflowHint =
        askDomain === 'construction-loan' ? ('construction_loan' as const) : ('real_estate' as const);

      const loadId = playbookAgentLoadRef.current + 1;
      playbookAgentLoadRef.current = loadId;

      const sessionKey = `${clause.id}-${Date.now()}`;
      const hasGrounding = clauseHasAskAgentGrounding(clause, askDomain);
      const needsContextSourceChoice = clauseNeedsAskAgentContextSourceChoice(clause);
      const askLanguage = detectAskAgentLanguage(clause);
      const initialQuestion =
        hasGrounding && !needsContextSourceChoice
          ? getAskAgentInitialQuestion(askDomain, askLanguage)
          : null;

      setPlaybookAgentBeside({
        sessionKey,
        clauseId: clause.id,
        clauseNumberDisplay,
        loading: true,
        error: null,
        contextBody: '',
        projectLabel,
        recordCount: 0,
        playbookRows: [],
        matchRankToArchiveRecordNum: new Map(),
        playbookBodyCached: '',
        askDomain,
        askLanguage,
        initialQuestion,
        openingAssistantMessage: null,
        awaitingDeepResearchConsent: false,
        awaitingContextSourceChoice: needsContextSourceChoice,
        contextSource: null,
      });

      try {
        const rows = await loadPlaybookArchive(resolvedProjectId, workflowHint);
        if (playbookAgentLoadRef.current !== loadId) return;

        const playbookBody = rows.length
          ? buildPlaybookContextFromRows(projectLabel, rows)
          : '(no playbook archive records for this project)';

        const contextSourceForBuild: AskAgentContextSource | null = needsContextSourceChoice
          ? null
          : 'both';
        const matchRankToArchiveRecordNum = buildMatchRankToArchiveRecordNum(
          clause.past_agreement?.options ?? [],
          buildPlaybookRecordNumberById(rows),
        );
        const clausePrefix = buildClauseAskAgentPrefix(
          clause,
          playbookPlainTextById,
          playbookFileNameById,
          askDomain,
          contextSourceForBuild,
          rows,
        );
        const includePlaybook = !needsContextSourceChoice;
        const contextBody = combineAskAgentContext(clausePrefix, playbookBody, {
          includePlaybookArchive: includePlaybook,
          contextSource: needsContextSourceChoice ? null : 'both',
        });

        if (!rows.length && !clausePrefix.trim()) {
          setPlaybookAgentBeside({
            sessionKey,
            clauseId: clause.id,
            clauseNumberDisplay,
            loading: false,
            error: 'No clause context or playbook records available for Ask Agent.',
            contextBody: '',
            projectLabel,
            recordCount: 0,
            playbookRows: [],
            matchRankToArchiveRecordNum: new Map(),
            playbookBodyCached: playbookBody,
            askDomain,
            askLanguage,
            initialQuestion: null,
            openingAssistantMessage: null,
            awaitingDeepResearchConsent: false,
            awaitingContextSourceChoice: false,
            contextSource: null,
          });
          return;
        }

        const askCopy = getAskAgentCopy(askLanguage);
        const openingAssistantMessage = needsContextSourceChoice
          ? askCopy.contextSourceQuestion
          : !hasGrounding
            ? rows.length > 0
              ? getAskAgentNoMatchesNotice(askDomain, askLanguage)
              : getAskAgentNoMatchesNoPlaybookNotice(askDomain, askLanguage)
            : null;

        setPlaybookAgentBeside({
          sessionKey,
          clauseId: clause.id,
          clauseNumberDisplay,
          loading: false,
          error: null,
          contextBody,
          projectLabel,
          recordCount: rows.length,
          playbookRows: rows,
          matchRankToArchiveRecordNum,
          playbookBodyCached: playbookBody,
          askDomain,
          askLanguage,
          initialQuestion,
          openingAssistantMessage,
          awaitingDeepResearchConsent: !hasGrounding && !needsContextSourceChoice && rows.length > 0,
          awaitingContextSourceChoice: needsContextSourceChoice,
          contextSource: null,
        });
      } catch (err: unknown) {
        if (playbookAgentLoadRef.current !== loadId) return;
        const msg =
          err instanceof Error ? err.message : 'Failed to load playbook for Ask Agent';
        setPlaybookAgentBeside({
          sessionKey,
          clauseId: clause.id,
          clauseNumberDisplay,
          loading: false,
          error: msg,
          contextBody: '',
          projectLabel,
          recordCount: 0,
          playbookRows: [],
          matchRankToArchiveRecordNum: new Map(),
          playbookBodyCached: '',
          askDomain,
          askLanguage,
          initialQuestion: null,
          openingAssistantMessage: null,
          awaitingDeepResearchConsent: false,
          awaitingContextSourceChoice: false,
          contextSource: null,
        });
      }
    },
    [
      projectId,
      projectIdProp,
      parentProjectName,
      playbookPlainTextById,
      playbookFileNameById,
      detectedDomain,
      handleCloseSalesAgreementSource,
      loadPlaybookArchive,
    ],
  );
  openPlaybookAgentRef.current = handleOpenPlaybookAgent;

  const handleChooseAskAgentContextSource = React.useCallback(
    (source: AskAgentContextSource) => {
      const agent = playbookAgentBeside;
      if (!agent?.awaitingContextSourceChoice) return;

      const clause = clauses.find((c) => c.id === agent.clauseId);
      if (!clause) return;

      const clausePrefix = buildClauseAskAgentPrefix(
        clause,
        playbookPlainTextById,
        playbookFileNameById,
        agent.askDomain,
        source,
        agent.playbookRows,
      );
      const includePlaybook =
        source === 'past_negotiations' || source === 'both';
      const contextBody = combineAskAgentContext(clausePrefix, agent.playbookBodyCached, {
        includePlaybookArchive: includePlaybook,
        contextSource: source,
      });
      const initialQuestion = getAskAgentInitialQuestionForSource(
        source,
        agent.askDomain,
        agent.askLanguage,
      );

      setPlaybookAgentBeside({
        ...agent,
        loading: false,
        error: null,
        contextBody,
        initialQuestion,
        openingAssistantMessage: null,
        awaitingContextSourceChoice: false,
        contextSource: source,
        awaitingDeepResearchConsent: false,
      });
    },
    [playbookAgentBeside, clauses, playbookPlainTextById, playbookFileNameById],
  );

  const handleApplyAgentSuggestedEdit = React.useCallback(
    async (chatMessages: PlaybookAgentChatTurn[]) => {
      const agentState = playbookAgentBeside;
      const resolvedProjectId = projectId ?? projectIdProp;
      if (!agentState?.clauseId || !resolvedProjectId) {
        throw new Error('Project or clause is not available');
      }
      const clause = clauses.find((c) => c.id === agentState.clauseId);
      if (!clause) {
        throw new Error('Clause not found');
      }

      setAgentAiStreamingClauseId(clause.id);
      try {
        const suggestedXml = await streamAiSuggestionFromChat(
          resolvedProjectId,
          clause,
          chatMessages,
          {
            contextSource: agentState.contextSource,
            askDomain: agentState.askDomain,
            askLanguage: agentState.askLanguage,
            playbookPlainTextById,
            playbookFileNameById,
          },
        );
        if (!suggestedXml) {
          throw new Error('No suggestion was generated');
        }
        setAgentSuggestedXmlByClauseId((prev) => ({ ...prev, [clause.id]: suggestedXml }));
      } finally {
        setAgentAiStreamingClauseId(null);
      }
    },
    [
      playbookAgentBeside,
      projectId,
      projectIdProp,
      clauses,
      playbookPlainTextById,
      playbookFileNameById,
    ],
  );

  /** Open persisted agent auto-apply findings in a read-only beside panel. */
  const handleOpenAgentAutoResult = React.useCallback(
    (clause: CrossCheckClause) => {
      const result = clause.ask_agent_auto_result;
      if (!result || !hasSavedAgentAutoResult(clause)) {
        setActionBanner({
          type: 'error',
          message: 'No saved agent recommendation for this card.',
        });
        return;
      }

      handleCloseSalesAgreementSource();

      const clauseNumberDisplay =
        clause.clause_number != null
          ? formatClauseNumberForDisplay(clause.clause_number)
          : null;
      const askDomain: AskAgentDomain =
        detectedDomain === 'construction-loan' ? 'construction-loan' : 'real-estate';
      const projectLabel =
        parentProjectName ||
        (askDomain === 'construction-loan' ? 'Past negotiations' : 'Negotiation archive');
      const askLanguage = result.language ?? detectAskAgentLanguage(clause);

      const initialMessages: PlaybookAgentChatTurn[] = (result.conversation || []).map(
        (turn, i) => ({
          id: `auto-${clause.id}-${i}`,
          role: turn.role,
          content: turn.content,
          ...(turn.role === 'assistant' && result.recommendation
            ? { recommendation: result.recommendation }
            : {}),
        }),
      );

      // If conversation was empty but explanation exists, show explanation alone.
      if (!initialMessages.length && result.explanation) {
        initialMessages.push({
          id: `auto-${clause.id}-explanation`,
          role: 'assistant',
          content: result.explanation,
          recommendation: result.recommendation,
        });
      }

      const sessionKey = `auto-${clause.id}-${result.applied_at || Date.now()}`;
      setPlaybookAgentBeside({
        sessionKey,
        clauseId: clause.id,
        clauseNumberDisplay,
        loading: false,
        error: null,
        contextBody: '(persisted agent auto-apply)',
        projectLabel,
        recordCount: 0,
        playbookRows: [],
        matchRankToArchiveRecordNum: new Map(),
        playbookBodyCached: '',
        askDomain,
        askLanguage,
        initialQuestion: null,
        openingAssistantMessage: null,
        awaitingDeepResearchConsent: false,
        awaitingContextSourceChoice: false,
        contextSource: result.context_source ?? null,
        readOnly: true,
        initialMessages,
      });

      // The replay needs the same archive rows and clause context the auto run used:
      // rows make "Record N" citations clickable, and the context body feeds feedback.
      const resolvedProjectId = projectId ?? projectIdProp;
      if (!resolvedProjectId) return;
      const workflowHint =
        askDomain === 'construction-loan' ? ('construction_loan' as const) : ('real_estate' as const);
      const loadId = playbookAgentLoadRef.current + 1;
      playbookAgentLoadRef.current = loadId;
      void (async () => {
        try {
          const rows = await loadPlaybookArchive(resolvedProjectId, workflowHint);
          if (playbookAgentLoadRef.current !== loadId) return;
          const playbookBody = rows.length
            ? buildPlaybookContextFromRows(projectLabel, rows)
            : '(no playbook archive records for this project)';
          const contextSource: AskAgentContextSource = result.context_source ?? 'both';
          const contextBody = buildAskAgentContextForClause(
            clause,
            rows,
            playbookBody,
            askDomain,
            playbookPlainTextById,
            playbookFileNameById,
            contextSource,
          );
          const matchRankToArchiveRecordNum = buildMatchRankToArchiveRecordNum(
            clause.past_agreement?.options ?? [],
            buildPlaybookRecordNumberById(rows),
          );
          setPlaybookAgentBeside((prev) =>
            prev && prev.sessionKey === sessionKey
              ? {
                  ...prev,
                  playbookRows: rows,
                  recordCount: rows.length,
                  matchRankToArchiveRecordNum,
                  playbookBodyCached: playbookBody,
                  contextBody: contextBody.trim() ? contextBody : prev.contextBody,
                }
              : prev,
          );
        } catch (err) {
          // Replay still works without rows; citations just stay non-clickable.
          console.warn('[CrossCheckResultsView] Failed to load playbook rows for agent replay:', err);
        }
      })();
    },
    [
      detectedDomain,
      parentProjectName,
      handleCloseSalesAgreementSource,
      projectId,
      projectIdProp,
      playbookPlainTextById,
      playbookFileNameById,
      loadPlaybookArchive,
    ],
  );
  openAgentAutoResultRef.current = handleOpenAgentAutoResult;

  const handleOpenSalesAgreementSource = React.useCallback(
    async (clause: CrossCheckClause, searchTextOverride?: string) => {
      // Same payload as dashboard card-header click: full SA text + clause number
      // so /pdf-previewer/highlight-clauses can use clause-number anchoring.
      const text = searchTextOverride?.trim()
        ? searchTextOverride.trim()
        : buildClauseHighlightSearchText(clause.sales_agreement, clause.clause_number);
      if (!subProjectId || !text) return;

      // Toggle off when the badge is clicked on the currently-active clause.
      if (
        salesAgreementSourceView &&
        salesAgreementSourceView.clauseId === clause.id &&
        !salesAgreementSourceView.loading
      ) {
        handleCloseSalesAgreementSource({ alignClause: true });
        return;
      }

      const clauseNumberDisplay =
        clause.clause_number != null
          ? formatClauseNumberForDisplay(clause.clause_number)
          : null;
      const clauseNumber =
        clauseNumberDisplay && clauseNumberDisplay !== '?'
          ? String(clause.clause_number).trim()
          : undefined;

      handleClosePlaybookAgent();

      salesAgreementSourceTargetRef.current = clause.id;
      setSalesAgreementSourceView({
        clauseId: clause.id,
        clauseNumberDisplay,
        clauseNumber,
        loading: true,
        error: null,
        buffer: null,
        page: 1,
        total: 1,
        searchText: text,
      });

      try {
        const { blob, highlightPage, totalPages } =
          await apiService.getSalesAgreementSourcePdf(
            subProjectId,
            text,
            detectedDomain ?? 'real-estate',
          );
        const buffer = await blob.arrayBuffer();
        if (salesAgreementSourceTargetRef.current !== clause.id) return;
        setSalesAgreementSourceView({
          clauseId: clause.id,
          clauseNumberDisplay,
          clauseNumber,
          loading: false,
          error: null,
          buffer,
          page: highlightPage,
          total: totalPages,
          searchText: text,
        });
      } catch (err: any) {
        if (salesAgreementSourceTargetRef.current !== clause.id) return;
        const detail =
          err?.response?.data?.detail ||
          (typeof err?.message === 'string' ? err.message : null) ||
          'Failed to load Sales Agreement source PDF';
        setSalesAgreementSourceView({
          clauseId: clause.id,
          clauseNumberDisplay,
          clauseNumber,
          loading: false,
          error: typeof detail === 'string' ? detail : 'Failed to load Sales Agreement source PDF',
          buffer: null,
          page: 1,
          total: 1,
          searchText: text,
        });
      }
    },
    [
      subProjectId,
      detectedDomain,
      salesAgreementSourceView,
      handleCloseSalesAgreementSource,
      handleClosePlaybookAgent,
    ],
  );

  // If the user switches tab while the split panel is open, close it — the
  // panel is bound to a specific clause that may not be in the new tab.
  React.useEffect(() => {
    if (!salesAgreementSourceView && !playbookAgentBeside) return;
    handleCloseSalesAgreementSource();
    handleClosePlaybookAgent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  React.useEffect(() => {
    loadResults();
  }, [loadResults]);

  // Poll while the cross-check is active.
  //
  // Design note: we intentionally do NOT stop immediately when the initial
  // status is "completed" — that status may be stale from a previous run.
  // The MCC / Construction-Loan flow calls onComplete() (which mounts this
  // component) *before* autoStartCrossCheck fires, so the backend can still
  // show the old "completed" state for several seconds while the new run is
  // being prepared.  We therefore let the interval run and decide to stop
  // only after:
  //   a) we have observed a non-terminal status followed by a terminal one
  //      (genuine completion of the new run), OR
  //   b) we have seen 3 consecutive terminal polls without any non-terminal
  //      in between (old, already-finished sub-project — stop to avoid
  //      hammering the API).
  //
  // Step 1/2 persist `status: "completed"` with workflow_step 1 or 2. That is
  // mid-workflow, not terminal — only Step 3 completion (or error) stops us.
  React.useEffect(() => {
    if (!subProjectId) return;

    // Base cadence + safety caps. A failing poll must never run forever: on auth
    // failure we stop immediately (re-auth required), and on transient errors we
    // back off exponentially and give up after a few consecutive failures. This
    // is the client-side guard against the 2026-06-22 arnon-tl tokenless 401 storm.
    const BASE_INTERVAL_MS = 3000;
    const MAX_BACKOFF_MS = 60000;
    const MAX_CONSECUTIVE_ERRORS = 5;

    let stopped = false;
    let hasSeenNonTerminal = false;
    let consecutiveTerminalPolls = 0;
    let consecutiveErrors = 0;
    let timeoutId: number | undefined;

    // Splits the gap into the two things it can possibly be, so the next run
    // does not need another round of inference.
    //
    // The poll asks for 3s between ticks, but after cross-check completes two
    // consecutive ticks have landed 61s apart (measured 2026-10-10, twice),
    // which is most of the 124s before agent auto-apply starts. Either the
    // timer fires late — the task pane is a webview, and a backgrounded one
    // clamps timers to roughly once a minute — or it fires on time and the
    // tick itself is slow. `lateBy` and `tickMs` cannot both be small.
    const schedule = (delayMs: number) => {
      if (stopped) return;
      const scheduledAt = Date.now();
      timeoutId = window.setTimeout(() => {
        const lateBy = Date.now() - scheduledAt - delayMs;
        if (lateBy > 2000) {
          log('[CrossCheck] Poll timer fired late', {
            askedMs: delayMs,
            lateBy,
            // A backgrounded webview is the usual reason a timer is clamped.
            visibility: typeof document !== 'undefined' ? document.visibilityState : 'unknown',
            hidden: typeof document !== 'undefined' ? document.hidden : null,
            hasFocus:
              typeof document !== 'undefined' && typeof document.hasFocus === 'function'
                ? document.hasFocus()
                : null,
          });
        }
        void poll();
      }, delayMs);
    };

    // Signature of the last progress we rendered in full; see crossCheckProgressSignature.
    let lastRenderedSignature: string | null = null;

    // Each tick reads only the progress (~1 KB). The full sub-project — multi-MB
    // once results exist — is re-fetched only when the progress changes in a
    // way worth rendering (never for a heartbeat), instead of every tick.
    const readTick = async (): Promise<LoadResult> => {
      let progress: MccWorkflowProgress;
      const progressStartedAt = Date.now();
      let progressRequestMs = 0;
      try {
        const res = await apiService.getCrossCheckProgress(
          subProjectId,
          detectedDomainRef.current ?? undefined,
        );
        progress = (res.data?.cross_check_progress ?? null) as MccWorkflowProgress;
        progressRequestMs = Date.now() - progressStartedAt;
      } catch (err) {
        const status = (err as { response?: { status?: number } })?.response?.status;
        const authError = Boolean((err as { isAuthError?: boolean })?.isAuthError) || status === 401;
        return { ok: false, authError };
      }
      const signature = crossCheckProgressSignature(progress);
      if (signature === lastRenderedSignature) {
        return { ok: true, authError: false, progress };
      }
      // This poll schedules itself 3s after each tick, but ticks that reload
      // were landing 60s apart, and the server answered both calls in under a
      // second — so the minute is spent here, client-side. Agent auto-apply
      // waits behind these reloads, so time this rather than guess at it.
      const reloadStartedAt = Date.now();
      const full = await loadResults(true);
      const reloadMs = Date.now() - reloadStartedAt;
      if (reloadMs > 5000) {
        log('[CrossCheck] Slow silent reload', {
          reloadMs,
          // Time the progress request took, so a slow network is
          // distinguishable from slow parsing/rendering of the payload.
          progressMs: progressRequestMs,
          clauseCount: clausesRef.current.length,
        });
      }
      // Only remember it once rendered, so a failed full load is retried.
      if (full.ok) lastRenderedSignature = signature;
      return full;
    };

    const poll = async () => {
      if (stopped) return;
      const tickStartedAt = Date.now();
      const result = await readTick();
      const tickMs = Date.now() - tickStartedAt;
      if (tickMs > 5000) {
        log('[CrossCheck] Slow poll tick', { tickMs, reloaded: result.ok });
      }
      if (stopped) return;

      // Auth failure: token can't be silently refreshed → stop and let the user
      // re-authenticate. Continuing would spam the API with tokenless requests.
      if (result.authError) {
        log('CrossCheckResultsView: polling stopped (auth failure — re-auth required)');
        stopped = true;
        return;
      }

      // Transient failure: back off, and bail out after too many in a row.
      if (!result.ok) {
        consecutiveErrors += 1;
        if (consecutiveErrors >= MAX_CONSECUTIVE_ERRORS) {
          log('CrossCheckResultsView: polling stopped (too many consecutive errors)', {
            consecutiveErrors,
          });
          stopped = true;
          setPollingError(false);
          setPollingStopped(true);
          return;
        }
        const backoff = Math.min(BASE_INTERVAL_MS * 2 ** consecutiveErrors, MAX_BACKOFF_MS);
        schedule(backoff);
        return;
      }

      consecutiveErrors = 0;
      setPollingError(false);
      setPollingStopped(false);

      const isTerminal = isCrossCheckWorkflowTerminal(result.progress ?? null);

      if (!isTerminal) {
        hasSeenNonTerminal = true;
        consecutiveTerminalPolls = 0;
      } else {
        consecutiveTerminalPolls += 1;
        if (hasSeenNonTerminal || consecutiveTerminalPolls >= 3) {
          stopped = true;
          return;
        }
      }

      schedule(BASE_INTERVAL_MS);
    };

    schedule(BASE_INTERVAL_MS);
    return () => {
      stopped = true;
      if (timeoutId !== undefined) window.clearTimeout(timeoutId);
    };
  }, [subProjectId, loadResults, pollRetryKey]);

  const stepStatuses = React.useMemo((): { step0: StepStatus; step1: StepStatus; step2: StepStatus } => {
    const hasCrossCheckResults = clauses.some((c) => {
      if (Boolean(c.sales_agreement) || Boolean(c.bank_of_changes)) return true;
      if (Array.isArray(c.insights) && c.insights.length > 0) return true;
      const past = c.past_agreement?.options;
      if (Array.isArray(past) && past.length > 0) return true;
      return false;
    });

    return resolveMccWorkflowStepStatuses({
      loading,
      progress: workflowProgressBlob,
      workflowStatus,
      workflowStep,
      hasIdentifiedClauses: clauses.length > 0,
      hasCrossCheckResults,
    });
  }, [workflowStep, workflowStatus, workflowProgressBlob, clauses, loading]);

  /** Which step, if any, the results are still waiting on — cards stay hidden until none. */
  const awaitingStage = clauseCardsHoldStage(stepStatuses, {
    startDeclined: crossCheckStartDeclined,
  });
  const awaitingResults = awaitingStage !== null;

  // Load agent auto-apply preference (default off). Prefer /auth/me; fall back to cached user.
  React.useEffect(() => {
    let cancelled = false;
    const fromCache = (): boolean => {
      try {
        const raw = tokenStorage.getUser();
        if (!raw) return false;
        const user = JSON.parse(raw) as {
          user_metadata?: { preferences?: { agent_auto_apply_enabled?: boolean } };
        };
        return user?.user_metadata?.preferences?.agent_auto_apply_enabled === true;
      } catch {
        return false;
      }
    };
    setAgentAutoApplyEnabled(fromCache());
    void (async () => {
      try {
        const response = await apiService.getCurrentUser();
        if (cancelled) return;
        if (response.success && response.data) {
          const enabled =
            response.data.user_metadata?.preferences?.agent_auto_apply_enabled === true;
          setAgentAutoApplyEnabled(enabled);
          try {
            tokenStorage.setUser(
              JSON.stringify({
                id: response.data.id,
                email: response.data.email,
                name: response.data.name,
                full_name: response.data.full_name,
                user_metadata: response.data.user_metadata,
              }),
            );
          } catch {
            /* ignore cache write */
          }
        }
      } catch {
        /* keep cached value */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [subProjectId]);

  // Snapshot refs so the pipeline trigger effect does not abort on every clause update.
  // (clausesRef is declared earlier for paragraph sync — reused here.)
  const reviewModeRef = React.useRef(reviewMode);
  React.useEffect(() => {
    reviewModeRef.current = reviewMode;
  }, [reviewMode]);
  const playbookFinalVersionByIdRef = React.useRef(playbookFinalVersionById);
  React.useEffect(() => {
    playbookFinalVersionByIdRef.current = playbookFinalVersionById;
  }, [playbookFinalVersionById]);
  const playbookPlainTextByIdRef = React.useRef(playbookPlainTextById);
  React.useEffect(() => {
    playbookPlainTextByIdRef.current = playbookPlainTextById;
  }, [playbookPlainTextById]);
  const playbookFileNameByIdRef = React.useRef(playbookFileNameById);
  React.useEffect(() => {
    playbookFileNameByIdRef.current = playbookFileNameById;
  }, [playbookFileNameById]);
  const parentProjectNameRef = React.useRef(parentProjectName);
  React.useEffect(() => {
    parentProjectNameRef.current = parentProjectName;
  }, [parentProjectName]);
  const detectedDomainRefForAuto = React.useRef(detectedDomain);
  React.useEffect(() => {
    detectedDomainRefForAuto.current = detectedDomain;
  }, [detectedDomain]);

  // Reset auto-apply session gate when navigating to a different sub-project.
  React.useEffect(() => {
    agentAutoApplyAbortRef.current = true;
    agentAutoApplyRunningRef.current = false;
    agentAutoApplyStartedForSubProjectRef.current = null;
    setAgentPhaseProgress(INITIAL_AGENT_PHASE_PROGRESS);
    setAgentAutoApplyArmed(false);
    agentArmedAtRef.current = null;
    // Allow a fresh run for the new sub-project after reset settles.
    const t = window.setTimeout(() => {
      agentAutoApplyAbortRef.current = false;
    }, 0);
    return () => {
      window.clearTimeout(t);
      agentAutoApplyAbortRef.current = true;
    };
  }, [subProjectId]);

  // After step 3 completes, run agent auto-apply when preference is ON.
  React.useEffect(() => {
    // Hidden behind another tab: the run starts, or goes on, when the view is shown again.
    if (!active) return;
    if (!agentAutoApplyEnabled) return;
    if (!subProjectId) return;
    if (loading) return;
    if (stepStatuses.step2 !== 'complete') return;

    // Armed before the remaining gates, not after: the wait this is meant to
    // show happens inside them.
    if (agentAutoApplyStartedForSubProjectRef.current !== subProjectId) {
      if (agentArmedAtRef.current == null) agentArmedAtRef.current = Date.now();
      setAgentAutoApplyArmed(true);
    }

    if (playbooksBatchLoading) return;

    const resolvedProjectId = projectId ?? projectIdProp;
    if (!resolvedProjectId) return;

    if (agentAutoApplyStartedForSubProjectRef.current === subProjectId) return;
    if (agentAutoApplyRunningRef.current) return;

    const snapshot = clausesRef.current;
    if (snapshot.length === 0) return;

    // Seed the step rows from what's actually persisted before deciding whether
    // to launch a run — this is what makes a prior run's completion show as
    // done (✓) after a reload, instead of sitting at "pending" (○) forever
    // because no pipeline run ever fires an onProgress event to correct it.
    setAgentPhaseProgress(computeAgentPhaseProgressFromClauses(snapshot));

    // Work can be pending at any of the three stages (not just "needs Ask"):
    // a prior run may have been interrupted after persisting an Ask result but
    // before generating or submitting the suggested edit. Checking only the
    // Ask-eligible set here would leave that clause stuck forever, since the
    // gate below marks this sub-project "started" and never re-evaluates it.
    const askPending = snapshot.filter(isClauseEligibleForAgentAutoApply);
    const editPending = snapshot.filter(isAgentEditPending);
    const submitPending = snapshot.filter((c) => isAgentSubmitPending(c));
    const hasPendingWork =
      askPending.length > 0 || editPending.length > 0 || submitPending.length > 0;
    const mode = reviewModeRef.current;
    if (!hasPendingWork && mode !== 'first_pass_legal_review') {
      agentAutoApplyStartedForSubProjectRef.current = subProjectId;
      setAgentAutoApplyArmed(false);
      return;
    }
    if (
      !hasPendingWork &&
      mode === 'first_pass_legal_review' &&
      !snapshot.some(
        (c) =>
          !c.lock_status?.locked &&
          !c.ask_agent_auto_result &&
          clauseMatchesAutoCloseCriteria(c, playbookFinalVersionByIdRef.current),
      )
    ) {
      agentAutoApplyStartedForSubProjectRef.current = subProjectId;
      setAgentAutoApplyArmed(false);
      return;
    }

    log('[CrossCheck] Agent gate opened', {
      waitedMs: agentArmedAtRef.current == null ? null : Date.now() - agentArmedAtRef.current,
      gateChecks: agentGateTick,
      clauseCount: snapshot.length,
    });
    agentAutoApplyStartedForSubProjectRef.current = subProjectId;
    agentAutoApplyRunningRef.current = true;
    agentAutoApplyAbortRef.current = false;
    const run = { hidden: false };
    agentAutoApplyRunRef.current = run;
    // A fresh/resumed run superseding a previous pause; clear it up front so
    // the paused banner doesn't linger under a running progress bar.
    setAgentPause(null);

    const askDomain: AskAgentDomain =
      detectedDomainRefForAuto.current === 'construction-loan'
        ? 'construction-loan'
        : 'real-estate';
    const projectLabel =
      parentProjectNameRef.current ||
      (askDomain === 'construction-loan' ? 'Past negotiations' : 'Negotiation archive');

    void (async () => {
      // Progress is shown by the agent step rows; only the final result uses the banner.
      try {
        await runAgentAutoApplyPipeline({
          subProjectId,
          projectId: resolvedProjectId,
          clauses: snapshot,
          reviewMode: mode,
          askDomain,
          projectLabel,
          playbookFinalVersionById: playbookFinalVersionByIdRef.current,
          playbookPlainTextById: playbookPlainTextByIdRef.current,
          playbookFileNameById: playbookFileNameByIdRef.current,
          shouldAbort: () => agentAutoApplyAbortRef.current || run.hidden,
          onProgress: (progress) => {
            // Stopped because the view was hidden: its "cancelled" or summary would be wrong, since the
            // run that starts when the view is shown again picks up the rest.
            if (run.hidden) return;
            setAgentPhaseProgress((prev) => reduceAgentPhaseProgress(prev, progress));
            if (progress.phase === 'done') {
              setActionBanner({
                type: progress.message.toLowerCase().includes('error') ? 'error' : 'success',
                message: progress.message,
              });
            } else if (progress.phase === 'paused' && progress.pause) {
              // Not a toast: this needs to stay visible with an action until
              // the user resumes, so it goes in its own persistent state
              // instead of the auto-dismissing actionBanner.
              setAgentPause(progress.pause);
            }
          },
          onClauseUpdate: (update) => {
            setClauses((prev) =>
              prev.map((c) => {
                if (c.id !== update.clauseId) return c;
                return {
                  ...c,
                  ...(update.askAgentAutoResult
                    ? { ask_agent_auto_result: update.askAgentAutoResult }
                    : {}),
                  ...(update.locked
                    ? {
                        lock_status: {
                          ...(c.lock_status || {}),
                          locked: true,
                          locked_at: new Date().toISOString(),
                        },
                      }
                    : {}),
                  ...(update.responseSubmitted
                    ? {
                        submission_status: {
                          ...(c.submission_status || {
                            schedule_submitted: false,
                            response_submitted: false,
                          }),
                          response_submitted: true,
                          response_submitted_at: new Date().toISOString(),
                        },
                      }
                    : {}),
                };
              }),
            );
            if (update.suggestedXml) {
              setAgentSuggestedXmlByClauseId((prev) => ({
                ...prev,
                [update.clauseId]: update.suggestedXml!,
              }));
            }
          },
        });
      } catch (err) {
        console.error('[CrossCheckResultsView] Agent auto-apply failed:', err);
        setActionBanner({
          type: 'error',
          message: 'Agent auto-apply failed. You can still use Ask Agent manually from each card.',
        });
      } finally {
        agentAutoApplyRunningRef.current = false;
        setAgentAutoApplyArmed(false);
        if (agentAutoApplyRunRef.current === run) agentAutoApplyRunRef.current = null;
        // Shown again before this run reached its stopping point: the gate opens again now that it has.
        if (run.hidden && activeRef.current) {
          agentAutoApplyStartedForSubProjectRef.current = null;
          setAgentResumeTick((n) => n + 1);
        }
      }
    })();
  }, [
    agentAutoApplyEnabled,
    subProjectId,
    loading,
    stepStatuses.step2,
    playbooksBatchLoading,
    projectId,
    projectIdProp,
    // Length only — avoid aborting/restarting when individual clause fields update.
    clauses.length,
    // Bumped by the "Resume" action after a usage-limit pause; nothing else
    // in this array changes on its own once the run has already "started"
    // for this sub-project, so without this the effect would never re-fire.
    agentResumeTick,
    // Bumped while armed, so the gate is re-checked on its own clock instead
    // of only when a poll tick happens to change one of the above.
    agentGateTick,
    active,
  ]);

  // Hidden behind another tab, the view keeps its state but does no work in Word, as when leaving the tab
  // took it away: the agent run stops at its next step. Shown again, the document may have been edited on
  // the other tab, so the paragraphs are found again, and the agent picks up what is still pending.
  const wasHiddenRef = React.useRef(!active);
  React.useEffect(() => {
    if (!active) {
      wasHiddenRef.current = true;
      if (agentAutoApplyRunRef.current) agentAutoApplyRunRef.current.hidden = true;
      return;
    }
    if (!wasHiddenRef.current) return;
    wasHiddenRef.current = false;
    // A run still on its way to its stopping point opens the gate itself when it gets there.
    if (!agentAutoApplyRunRef.current) {
      agentAutoApplyStartedForSubProjectRef.current = null;
      setAgentResumeTick((n) => n + 1);
    }
    runParagraphSync(clausesRef.current);
  }, [active, runParagraphSync]);

  /**
   * Resume after a pause (usage limit or session expiry). The pipeline never
   * marked the clauses it didn't reach as done (see
   * runAgentAutoApplyPipeline's checkForPause), so re-opening this effect's
   * gate is enough — it recomputes askPending/editPending/submitPending from
   * the current clauses and picks up exactly where the paused run stopped,
   * same as a reload would.
   */
  const resumeAgentAutoApplyAfterPause = React.useCallback(() => {
    setAgentPause(null);
    agentAutoApplyStartedForSubProjectRef.current = null;
    agentAutoApplyRunningRef.current = false;
    setAgentResumeTick((n) => n + 1);
  }, []);

  // Per-step timers — running while the view is shown, so the clock is live from mount
  React.useEffect(() => {
    if (!active) return undefined;
    setTimerNowMs(Date.now());
    const id = setInterval(() => setTimerNowMs(Date.now()), 1000);
    return () => clearInterval(id);
  }, [active]);

  React.useEffect(() => {
    const now = Date.now();
    const t = stepTimingRef.current;

    const applyStatus = (
      status: StepStatus,
      startKey: 'step1StartMs' | 'step2StartMs',
      elapsedKey: 'step1ElapsedSec' | 'step2ElapsedSec',
      fallbackStartMs?: number
    ) => {
      if (status === 'pending') {
        // Don't reset start if already ticking — keeps timer live if status briefly flickers
        if (!t[startKey]) t[elapsedKey] = 0;
        return;
      }
      if (status === 'running') {
        // Seed from fallback (mount time) if we've never seen this step start
        if (!t[startKey]) t[startKey] = fallbackStartMs ?? now;
        return;
      }
      // complete / error — freeze the elapsed time
      if (t[startKey]) {
        t[elapsedKey] = Math.max(1, Math.floor((now - t[startKey]!) / 1000));
        t[startKey] = null;
      }
    };

    applyStatus(stepStatuses.step1, 'step1StartMs', 'step1ElapsedSec', mountMsRef.current);
    applyStatus(stepStatuses.step2, 'step2StartMs', 'step2ElapsedSec');
  }, [stepStatuses.step1, stepStatuses.step2]);

  const formatStepSeconds = React.useCallback((totalSec: number): string => {
    const sec = Math.max(0, Math.floor(totalSec));
    const mm = String(Math.floor(sec / 60)).padStart(2, '0');
    const ss = String(sec % 60).padStart(2, '0');
    return `${mm}:${ss}`;
  }, []);

  const stepTimes = React.useMemo(() => {
    const t = stepTimingRef.current;
    const now = timerNowMs;
    const step1Sec =
      t.step1StartMs != null
        ? t.step1ElapsedSec + Math.floor((now - t.step1StartMs) / 1000)
        : t.step1ElapsedSec;
    const step2Sec =
      t.step2StartMs != null
        ? t.step2ElapsedSec + Math.floor((now - t.step2StartMs) / 1000)
        : t.step2ElapsedSec;
    return {
      step1: formatStepSeconds(step1Sec),
      step2: formatStepSeconds(step2Sec),
    };
  }, [timerNowMs, formatStepSeconds]);

  const hasConstructionLoanMatter = React.useMemo(() => (
    clauses.some((c) => {
      const matter = (c as any).matter;
      return matter === 'commercial' || matter === 'legal' || matter === 'other';
    })
  ), [clauses]);

  const isConstructionLoan = detectedDomain === 'construction-loan' || hasConstructionLoanMatter;

  // Treat any clause whose `matter` is not one of the three known matter
  // values as 'other' for tab grouping purposes (handles legacy records that
  // were classified before the matter taxonomy existed).
  const matterFor = React.useCallback((c: CrossCheckClause): MatterTabId => {
    const raw = (c as any).matter;
    if (raw === 'commercial' || raw === 'legal' || raw === 'other') {
      return raw;
    }
    return 'other';
  }, []);

  const filteredClauses = React.useMemo(() => {
    if (activeTab === 'all') return clauses;
    if (isConstructionLoan) {
      return clauses.filter((c) => matterFor(c) === activeTab);
    }
    if (activeTab === 'saved') return clauses.filter((c) => c.documentType === 'saved_for_later');
    return clauses.filter((c) => c.documentType === (activeTab as CrossCheckDocumentType));
  }, [clauses, activeTab, isConstructionLoan, matterFor]);

  const showClauseSubFilters = React.useMemo(() => {
    if (isConstructionLoan) return true;
    return clauses.some((c) => (c.past_agreement?.options?.length ?? 0) > 0);
  }, [isConstructionLoan, clauses]);

  const hasActiveClauseSubFilters = filterFinalVersion || filterMinSimilarity != null;
  // Read by the sticky handler above: it forces the tab to "all" but leaves
  // these alone, so a filtered-out card is never rendered and the pending
  // scroll has no header to flush to.
  stickyFilterStateRef.current = {
    hasActiveClauseSubFilters,
    displayedCount: displayedClausesRef.current,
  };
  const hasActiveSubFilters =
    hasActiveClauseSubFilters || filterSelectedAuthors !== null || filterSelectedYears !== null;

  const availableAuthors = React.useMemo(
    () => collectPastNegotiationAuthors(filteredClauses, playbookAuthorById),
    [filteredClauses, playbookAuthorById],
  );

  const availableYears = React.useMemo(
    () => collectPastNegotiationYears(filteredClauses, playbookTimestampById),
    [filteredClauses, playbookTimestampById],
  );

  const authorFilterLabel = React.useMemo(
    () => formatAuthorFilterLabel(filterSelectedAuthors),
    [filterSelectedAuthors],
  );

  const timeFilterLabel = React.useMemo(
    () => formatTimeFilterLabel(filterSelectedYears),
    [filterSelectedYears],
  );

  const displayedClauses = React.useMemo(() => {
    const base = !hasActiveClauseSubFilters
      ? filteredClauses
      : applyClauseEditorFilters(filteredClauses, {
          finalVersion: filterFinalVersion,
          minSimilarity: filterMinSimilarity,
          finalVersionById: playbookFinalVersionById,
        });

    // Order the cards to match the physical order of the clauses in the Word
    // document. Paragraph sync resolves each clause to a body paragraph index;
    // we sort by that ascending so the panel mirrors the document top-to-bottom.
    // Clauses that couldn't be resolved to a paragraph (e.g. "לא נמצא", or
    // saved-for-later requests that aren't in the doc) keep their original
    // relative order and sink to the bottom. The index tie-break makes this a
    // stable sort (paragraphs sharing an index, like table rows, stay in order).
    return base
      .map((clause, index) => ({ clause, index }))
      .sort((a, b) => {
        const ai = Number(a.clause.paragraph_index);
        const bi = Number(b.clause.paragraph_index);
        const aValid = Number.isFinite(ai);
        const bValid = Number.isFinite(bi);
        if (aValid && bValid) return ai - bi || a.index - b.index;
        if (aValid) return -1;
        if (bValid) return 1;
        return a.index - b.index;
      })
      .map((entry) => entry.clause);
  }, [
    filteredClauses,
    filterFinalVersion,
    filterMinSimilarity,
    hasActiveClauseSubFilters,
    playbookFinalVersionById,
  ]);

  displayedClausesRef.current = displayedClauses.length;

  // Flush sticky scroll after tab/filter re-render exposes the target card.
  React.useEffect(() => {
    const pending = pendingStickyScrollClauseIdRef.current;
    if (!pending) return;
    if (!clauseCardHeaderRefs.current.has(pending)) return;
    scrollStickyClauseIntoView(pending);
    pendingStickyScrollClauseIdRef.current = null;
  }, [displayedClauses, activeTab, scrollStickyClauseIntoView]);

  const closableCount = React.useMemo(
    () => displayedClauses.filter(isClauseClosable).length,
    [displayedClauses],
  );

  const openableCount = React.useMemo(
    () => displayedClauses.filter((clause) => clause.lock_status?.locked).length,
    [displayedClauses],
  );

  const performBulkSetLock = React.useCallback(
    async (clausesToUpdate: CrossCheckClause[], locked: boolean): Promise<number> => {
      if (!subProjectId || !clausesToUpdate.length) return 0;

      const clauseIds = clausesToUpdate.map((clause) => clause.id).filter(Boolean) as string[];
      if (!clauseIds.length) return 0;

      const response = await apiService.setClausesLock(subProjectId, clauseIds, locked);
      const updatedIds = new Set(response?.data?.clause_ids ?? []);

      if (updatedIds.size > 0) {
        setClauses((prev) =>
          prev.map((c) =>
            updatedIds.has(c.id)
              ? { ...c, lock_status: { ...(c.lock_status || {}), locked } }
              : c
          )
        );
      }

      return updatedIds.size;
    },
    [subProjectId],
  );

  const performBulkLock = React.useCallback(
    async (clausesToLock: CrossCheckClause[]): Promise<number> =>
      performBulkSetLock(clausesToLock, true),
    [performBulkSetLock],
  );

  const handleOpenAllFiltered = React.useCallback(() => {
    if (!subProjectId || bulkOpening || bulkClosing) return;
    const toOpen = displayedClauses.filter((clause) => clause.lock_status?.locked);
    if (!toOpen.length) return;

    void (async () => {
      setBulkOpening(true);
      setActionBanner({
        type: 'loading',
        message: `Opening ${toOpen.length} clause${toOpen.length === 1 ? '' : 's'}…`,
      });
      try {
        const openedCount = await performBulkSetLock(toOpen, false);
        if (openedCount === toOpen.length) {
          setActionBanner({
            type: 'success',
            message: `Opened ${openedCount} clause${openedCount === 1 ? '' : 's'}.`,
          });
        } else if (openedCount > 0) {
          setActionBanner({
            type: 'error',
            message: `Opened ${openedCount} of ${toOpen.length} clauses. Some could not be unlocked.`,
          });
        } else {
          setActionBanner({
            type: 'error',
            message: 'Failed to open the selected clauses.',
          });
        }
      } catch (err: any) {
        console.error('[CrossCheckResultsView] Bulk open failed:', err);
        setActionBanner({
          type: 'error',
          message: 'Failed to open the selected clauses.',
        });
      } finally {
        setBulkOpening(false);
      }
    })();
  }, [subProjectId, bulkOpening, bulkClosing, displayedClauses, performBulkSetLock]);

  const handleCloseFiltered = React.useCallback(() => {
    if (!subProjectId || bulkClosing || bulkOpening || !hasActiveClauseSubFilters) return;
    const toLock = displayedClauses.filter(isClauseClosable);
    if (!toLock.length) return;

    const { withTrackChanges, withoutTrackChanges } = partitionClosableByTrackChanges(toLock);
    if (withTrackChanges.length > 0) {
      setBulkClosePrompt({ toLock, withTrackChanges, withoutTrackChanges });
      return;
    }

    void (async () => {
      setBulkClosing(true);
      setActionBanner({
        type: 'loading',
        message: `Closing ${toLock.length} clause${toLock.length === 1 ? '' : 's'}…`,
      });
      try {
        const lockedCount = await performBulkLock(toLock);
        if (lockedCount === toLock.length) {
          setActionBanner({
            type: 'success',
            message: `Closed ${lockedCount} clause${lockedCount === 1 ? '' : 's'}.`,
          });
        } else if (lockedCount > 0) {
          setActionBanner({
            type: 'error',
            message: `Closed ${lockedCount} of ${toLock.length} clauses. Some could not be locked.`,
          });
        } else {
          setActionBanner({
            type: 'error',
            message: 'Failed to close the selected clauses.',
          });
        }
      } catch (err: any) {
        console.error('[CrossCheckResultsView] Bulk close failed:', err);
        setActionBanner({
          type: 'error',
          message: 'Failed to close the selected clauses.',
        });
      } finally {
        setBulkClosing(false);
      }
    })();
  }, [subProjectId, bulkClosing, bulkOpening, hasActiveClauseSubFilters, displayedClauses, performBulkLock]);

  const handleBulkCloseWithoutAccept = React.useCallback(async () => {
    if (!bulkClosePrompt || bulkClosing) return;
    const { toLock } = bulkClosePrompt;
    if (!toLock.length) {
      setBulkClosePrompt(null);
      return;
    }

    setBulkClosing(true);
    setBulkCloseBusyMessage(
      `Closing ${toLock.length} clause${toLock.length === 1 ? '' : 's'}…`,
    );
    try {
      const lockedCount = await performBulkLock(toLock);
      if (lockedCount === toLock.length) {
        setActionBanner({
          type: 'success',
          message: `Closed ${lockedCount} clause${lockedCount === 1 ? '' : 's'} without accepting track changes.`,
        });
      } else if (lockedCount > 0) {
        setActionBanner({
          type: 'error',
          message: `Closed ${lockedCount} of ${toLock.length} clauses. Some could not be locked.`,
        });
      } else {
        setActionBanner({
          type: 'error',
          message: 'Failed to close the selected clauses.',
        });
      }
    } catch (err: any) {
      console.error('[CrossCheckResultsView] Bulk close without accept failed:', err);
      setActionBanner({
        type: 'error',
        message: 'Failed to close the selected clauses.',
      });
    } finally {
      setBulkClosing(false);
      setBulkCloseBusyMessage(null);
      setBulkClosePrompt(null);
    }
  }, [bulkClosePrompt, bulkClosing, performBulkLock]);

  const handleBulkCloseAcceptAndClose = React.useCallback(async () => {
    if (!subProjectId || !bulkClosePrompt || bulkClosing) return;
    const { toLock, withTrackChanges } = bulkClosePrompt;

    setBulkClosing(true);
    setBulkCloseBusyMessage(
      `Accepting track changes for ${withTrackChanges.length} clause${withTrackChanges.length === 1 ? '' : 's'}…`,
    );

    let acceptedCount = 0;
    try {
      const acceptedIds = await acceptClausesSequentially(
        subProjectId,
        withTrackChanges,
        (current, total) => {
          setBulkCloseBusyMessage(`Accepting track changes (${current}/${total})…`);
        },
      );
      acceptedCount = acceptedIds.length;
      for (const clauseId of acceptedIds) {
        handleResponseSubmitted(clauseId);
      }

      setBulkCloseBusyMessage(
        `Closing ${toLock.length} clause${toLock.length === 1 ? '' : 's'}…`,
      );
      const lockedCount = await performBulkLock(toLock);

      if (lockedCount === toLock.length) {
        const acceptNote =
          acceptedCount < withTrackChanges.length
            ? ` Accepted ${acceptedCount} of ${withTrackChanges.length} track-change clause${withTrackChanges.length === 1 ? '' : 's'}.`
            : '';
        setActionBanner({
          type: 'success',
          message: `Accepted changes and closed ${lockedCount} clause${lockedCount === 1 ? '' : 's'}.${acceptNote}`,
        });
      } else if (lockedCount > 0) {
        setActionBanner({
          type: 'error',
          message: `Closed ${lockedCount} of ${toLock.length} clauses after accepting track changes.`,
        });
      } else {
        setActionBanner({
          type: 'error',
          message: 'Failed to close the selected clauses after accepting track changes.',
        });
      }
    } catch (err: any) {
      console.error('[CrossCheckResultsView] Bulk accept and close failed:', err);
      setActionBanner({
        type: 'error',
        message: 'Failed to accept track changes and close the selected clauses.',
      });
    } finally {
      setBulkClosing(false);
      setBulkCloseBusyMessage(null);
      setBulkClosePrompt(null);
    }
  }, [
    subProjectId,
    bulkClosePrompt,
    bulkClosing,
    performBulkLock,
    handleResponseSubmitted,
  ]);

  const applySimilarityThreshold = React.useCallback((threshold: number) => {
    const clamped = Math.min(SIMILARITY_FILTER_MAX, Math.max(SIMILARITY_FILTER_MIN, threshold));
    setSimilaritySliderValue(clamped);
    setFilterMinSimilarity(clamped);
  }, []);

  const handleSimilarityChipClick = React.useCallback(() => {
    setSimilarityPanelOpen((open) => {
      const nextOpen = !open;
      if (nextOpen) {
        const nextThreshold = filterMinSimilarity ?? SIMILARITY_FILTER_MAX;
        setSimilaritySliderValue(nextThreshold);
        setFilterMinSimilarity(nextThreshold);
      }
      return nextOpen;
    });
  }, [filterMinSimilarity]);

  const handleSimilaritySliderChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      applySimilarityThreshold(Number(event.target.value));
    },
    [applySimilarityThreshold],
  );

  const handleClearSubFilters = React.useCallback(() => {
    setFilterFinalVersion(false);
    setFilterMinSimilarity(null);
    setSimilarityPanelOpen(false);
    setSimilaritySliderValue(SIMILARITY_FILTER_MAX);
    setFilterSelectedAuthors(null);
    setAuthorsPanelOpen(false);
    setFilterSelectedYears(null);
    setTimePanelOpen(false);
  }, []);

  const handleAuthorsChipClick = React.useCallback(() => {
    setAuthorsPanelOpen((open) => !open);
    setTimePanelOpen(false);
  }, []);

  const handleTimeChipClick = React.useCallback(() => {
    setTimePanelOpen((open) => !open);
    setAuthorsPanelOpen(false);
  }, []);

  const handleSelectAllTime = React.useCallback(() => {
    setFilterSelectedYears(null);
  }, []);

  const handleToggleYearFilter = React.useCallback(
    (year: number) => {
      setFilterSelectedYears((prev) => {
        if (prev === null) {
          const next = new Set(availableYears.filter((y) => y !== year));
          return next.size === 0 ? new Set() : next;
        }
        const next = new Set(prev);
        if (next.has(year)) next.delete(year);
        else next.add(year);
        if (next.size === 0) return new Set();
        if (next.size >= availableYears.length) return null;
        return next;
      });
    },
    [availableYears],
  );

  const handleClearTimeFilter = React.useCallback(() => {
    setFilterSelectedYears(null);
    setTimePanelOpen(false);
  }, []);

  const handleSelectAllAuthors = React.useCallback(() => {
    setFilterSelectedAuthors(null);
  }, []);

  const handleToggleAuthorFilter = React.useCallback(
    (author: string) => {
      setFilterSelectedAuthors((prev) => {
        if (prev === null) {
          const next = new Set(availableAuthors.filter((name) => name !== author));
          return next.size === 0 ? new Set() : next;
        }
        const next = new Set(prev);
        if (next.has(author)) next.delete(author);
        else next.add(author);
        if (next.size === 0) return new Set();
        if (next.size >= availableAuthors.length) return null;
        return next;
      });
    },
    [availableAuthors],
  );

  const handleClearAuthorsFilter = React.useCallback(() => {
    setFilterSelectedAuthors(null);
    setAuthorsPanelOpen(false);
  }, []);

  const handleClearSimilarityFilter = React.useCallback(() => {
    setFilterMinSimilarity(null);
    setSimilaritySliderValue(SIMILARITY_FILTER_MAX);
    setSimilarityPanelOpen(false);
  }, []);

  React.useEffect(() => {
    if (!similarityPanelOpen) return;
    const handlePointerDown = (event: MouseEvent) => {
      const root = similarityFilterRef.current;
      if (!root || root.contains(event.target as Node)) return;
      setSimilarityPanelOpen(false);
    };
    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, [similarityPanelOpen]);

  React.useEffect(() => {
    if (!authorsPanelOpen) return;
    const handlePointerDown = (event: MouseEvent) => {
      const root = authorsFilterRef.current;
      if (!root || root.contains(event.target as Node)) return;
      setAuthorsPanelOpen(false);
    };
    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, [authorsPanelOpen]);

  React.useEffect(() => {
    if (!timePanelOpen) return;
    const handlePointerDown = (event: MouseEvent) => {
      const root = timeFilterRef.current;
      if (!root || root.contains(event.target as Node)) return;
      setTimePanelOpen(false);
    };
    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, [timePanelOpen]);

  React.useEffect(() => {
    if (filterSelectedYears === null) return;
    const stale = Array.from(filterSelectedYears).some((year) => !availableYears.includes(year));
    if (!stale) return;
    const next = new Set(
      Array.from(filterSelectedYears).filter((year) => availableYears.includes(year)),
    );
    setFilterSelectedYears(next.size === 0 || next.size >= availableYears.length ? null : next);
  }, [availableYears, filterSelectedYears]);

  React.useEffect(() => {
    if (filterSelectedAuthors === null) return;
    const stale = Array.from(filterSelectedAuthors).some((author) => !availableAuthors.includes(author));
    if (stale) {
      const next = new Set(
        Array.from(filterSelectedAuthors).filter((author) => availableAuthors.includes(author)),
      );
      setFilterSelectedAuthors(next.size === 0 || next.size >= availableAuthors.length ? null : next);
    }
  }, [availableAuthors, filterSelectedAuthors]);

  const counts = React.useMemo(() => ({
    all: clauses.length,
    sales_agreement: clauses.filter((c) => c.documentType === 'sales_agreement').length,
    appendix_a: clauses.filter((c) => c.documentType === 'appendix_a').length,
    saved: clauses.filter((c) => c.documentType === 'saved_for_later').length,
    commercial: clauses.filter((c) => matterFor(c) === 'commercial').length,
    legal: clauses.filter((c) => matterFor(c) === 'legal').length,
    other: clauses.filter((c) => matterFor(c) === 'other').length,
  }), [clauses, matterFor]);

  // Progress = locked OR submitted to response OR submitted to schedule (same logic as courtyard dashboard)
  const progress = React.useMemo(() => {
    const total = clauses.length;
    const current = clauses.filter((c) => {
      const isLocked = c.lock_status?.locked ?? false;
      const isSubmittedToResponse = c.submission_status?.response_submitted ?? false;
      const isSubmittedToSchedule = c.submission_status?.schedule_submitted ?? false;
      return isLocked || isSubmittedToResponse || isSubmittedToSchedule;
    }).length;
    const pct = total ? Math.round((current / total) * 100) : 0;
    return { current, total, pct };
  }, [clauses]);

  // Construction Loan classifies each request by `matter` (commercial /
  // legal / other) instead of by document type, so the tabs differ.
  const tabs: { id: TabId; label: string; cssClass: string }[] = isConstructionLoan
    ? [
        { id: 'all', label: t('results.tabs.all', { count: counts.all }), cssClass: 'cc-tab-all' },
        { id: 'commercial', label: t('results.tabs.commercial', { count: counts.commercial }), cssClass: 'cc-tab-sales' },
        { id: 'legal', label: t('results.tabs.legal', { count: counts.legal }), cssClass: 'cc-tab-appendix' },
        { id: 'other', label: t('results.tabs.other', { count: counts.other }), cssClass: 'cc-tab-saved' },
      ]
    : [
        { id: 'all', label: t('results.tabs.all', { count: counts.all }), cssClass: 'cc-tab-all' },
        { id: 'sales_agreement', label: t('results.tabs.salesAgreement', { count: counts.sales_agreement }), cssClass: 'cc-tab-sales' },
        { id: 'appendix_a', label: t('results.tabs.appendixA', { count: counts.appendix_a }), cssClass: 'cc-tab-appendix' },
        { id: 'saved', label: t('results.tabs.saved', { count: counts.saved }), cssClass: 'cc-tab-saved' },
      ];

  // If the detected domain switches (e.g. CL → MCC) reset the tab so we don't
  // end up sticking on a tab id that no longer exists in the new tab set.
  React.useEffect(() => {
    const allowed = new Set<TabId>(tabs.map((t) => t.id));
    if (!allowed.has(activeTab)) {
      setActiveTab('all');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isConstructionLoan]);

  // When tab changes, scroll outer task pane so progress + tabs stay in view (single scroll: .clauses-view)
  React.useEffect(() => {
    const prev = prevActiveTabForScrollRef.current;
    if (prev === null) {
      prevActiveTabForScrollRef.current = activeTab;
      return;
    }
    if (prev === activeTab) {
      return;
    }
    prevActiveTabForScrollRef.current = activeTab;
    setFilterFinalVersion(false);
    setFilterMinSimilarity(null);
    setSimilarityPanelOpen(false);
    setSimilaritySliderValue(SIMILARITY_FILTER_MAX);
    stickyHeaderRef.current?.scrollIntoView({ block: 'start', behavior: 'smooth' });
  }, [activeTab]);

  const handleTabClick = React.useCallback((tabId: TabId) => {
    setActiveTab(tabId);
  }, []);

  const useTaskPaneBesidePortalLayout =
    mccTaskPaneBesideWire && !!besidePdfChromeInDock && besidePanelOpen;
  const pdfBesideDockHost = pdfBesideDockRef?.current ?? null;
  const besideHandleSlotHost = besideHandleSlotRef?.current ?? null;
  const showBesidePortal =
    useTaskPaneBesidePortalLayout && pdfBesideDockHost != null && besideHandleSlotHost != null;

  React.useLayoutEffect(() => {
    if (besidePanelOpen) {
      prevBesidePanelOpenRef.current = true;
      scheduleScrollActiveBesideClauseIntoView();
      return;
    }

    const wasOpen = prevBesidePanelOpenRef.current;
    prevBesidePanelOpenRef.current = false;
    if (!wasOpen) return;

    const clauseId = pendingCloseClauseScrollRef.current;
    pendingCloseClauseScrollRef.current = null;
    if (!clauseId) return;

    scheduleScrollClauseHeaderIntoView(clauseId);
    const settleTimer = window.setTimeout(
      () => scheduleScrollClauseHeaderIntoView(clauseId),
      160,
    );
    return () => window.clearTimeout(settleTimer);
  }, [
    playbookAgentBeside?.clauseId,
    playbookAgentBeside?.sessionKey,
    salesAgreementSourceView?.clauseId,
    besidePanelOpen,
    besidePdfChromeInDock,
    scheduleScrollActiveBesideClauseIntoView,
    scheduleScrollClauseHeaderIntoView,
  ]);

  /** Re-align focused clause header after Word add-in or split-pane resize settles. */
  React.useEffect(() => {
    if (!besidePanelOpen) return;

    const observeTargets: HTMLElement[] = [];
    const scrollRoot = besideRightScrollRef?.current;
    if (scrollRoot) observeTargets.push(scrollRoot);
    if (resultsViewRef.current) observeTargets.push(resultsViewRef.current);
    if (besideResizeOuterRef?.current) observeTargets.push(besideResizeOuterRef.current);
    if (observeTargets.length === 0) return;

    let debounceTimer: ReturnType<typeof setTimeout> | null = null;
    const onResizeSettled = () => {
      if (besideResizeDragRef.current) return;
      if (debounceTimer) clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => {
        debounceTimer = null;
        scheduleScrollActiveBesideClauseRef.current();
      }, 160);
    };

    let ro: ResizeObserver | undefined;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(onResizeSettled);
      observeTargets.forEach((el) => ro!.observe(el));
    }

    window.addEventListener('resize', onResizeSettled);

    return () => {
      ro?.disconnect();
      window.removeEventListener('resize', onResizeSettled);
      if (debounceTimer) clearTimeout(debounceTimer);
    };
  }, [
    besidePanelOpen,
    besideRightScrollRef,
    besideResizeOuterRef,
    playbookAgentBeside?.clauseId,
    salesAgreementSourceView?.clauseId,
    scheduleScrollActiveBesideClauseIntoView,
  ]);

  const besideResizeHandleEl = (
    <div
      className="cc-sa-beside-resize-handle"
      role="separator"
      aria-orientation="vertical"
      aria-label="Resize side panel and clause list"
      aria-valuemin={MIN_BESIDE_PDF_PCT}
      aria-valuemax={MAX_BESIDE_PDF_PCT}
      aria-valuenow={Math.round(saBesidePdfWidthPct)}
      tabIndex={0}
      title="Drag to resize. Double-click to reset. Arrow keys nudge."
      onPointerDown={handleBesideResizePointerDown}
      onPointerMove={handleBesideResizePointerMove}
      onPointerUp={endBesideResizeDrag}
      onPointerCancel={endBesideResizeDrag}
      onLostPointerCapture={() => {
        if (besideResizeDragRef.current) {
          besideResizeDragRef.current = null;
          setSaBesidePdfWidthPct((prev) => persistBesidePdfPct(prev));
          requestAnimationFrame(() => scheduleScrollActiveBesideClauseRef.current());
        }
      }}
      onKeyDown={handleBesideResizeKeyDown}
      onDoubleClick={handleBesideResizeDoubleClick}
    />
  );

  const renderBesideResizeHandlePortal = (portalTarget: HTMLElement | null) =>
    portalTarget ? createPortal(besideResizeHandleEl, portalTarget) : null;

  return (
    <div
      ref={resultsViewRef}
      className={`cc-results-view${
        besidePanelOpen ? ' cc-results-view--sa-source-open' : ''
      }${
        besidePanelOpen
          ? useTaskPaneBesidePortalLayout
            ? ' cc-results-view--sa-beside-portaled'
            : ' cc-results-view--sa-split-side-by-side'
          : ''
      }`}
      style={
        besidePanelOpen && !useTaskPaneBesidePortalLayout
          ? ({
              ['--cc-sa-beside-pdf-pct' as string]: `${saBesidePdfWidthPct}%`,
            } as React.CSSProperties)
          : undefined
      }
    >
      {salesAgreementSourceView &&
        (showBesidePortal ? (
          <>
            {createPortal(
              <div className="cc-sa-mcc-portal-pdf-root">
                <div
                  className="cc-sa-source-split-panel cc-sa-source-split-panel--side-by-side cc-sa-source-split-panel--mcc-docked"
                  role="region"
                  aria-label="Sales Agreement source PDF"
                >
                  <div className="cc-sa-source-split-header">
                    <span className="cc-sa-source-split-title">
                      Sales Agreement
                      {salesAgreementSourceView.clauseNumberDisplay
                        ? ` — Clause ${salesAgreementSourceView.clauseNumberDisplay}`
                        : ''}
                    </span>
                    <div className="cc-sa-source-split-header-actions">
                      <button
                        type="button"
                        className="cc-sa-source-split-close"
                        onClick={() => handleCloseSalesAgreementSource({ alignClause: true })}
                        aria-label="Close Sales Agreement viewer"
                        title="Close Sales Agreement viewer"
                      >
                        ✕
                      </button>
                    </div>
                  </div>
                  <div className="cc-sa-source-split-body">
                    {salesAgreementSourceView.loading && (
                      <div className="cc-source-loading" role="status" aria-live="polite">
                        <span className="cc-source-loading-spinner" aria-hidden />
                        <p className="cc-source-loading-text">Loading Sales Agreement…</p>
                      </div>
                    )}
                    {salesAgreementSourceView.error && (
                      <p className="cc-source-pdf-error">{salesAgreementSourceView.error}</p>
                    )}
                    {salesAgreementSourceView.buffer &&
                      !salesAgreementSourceView.loading &&
                      !salesAgreementSourceView.error && (
                        <PastNegotiationSourcePdfPanel
                          pdfData={salesAgreementSourceView.buffer}
                          initialPage={salesAgreementSourceView.page}
                          totalPagesHint={salesAgreementSourceView.total}
                          searchText={salesAgreementSourceView.searchText}
                          clauseNumber={salesAgreementSourceView.clauseNumber}
                        />
                      )}
                  </div>
                </div>
              </div>,
              pdfBesideDockHost,
            )}
            {renderBesideResizeHandlePortal(besideHandleSlotHost)}
          </>
        ) : (
          <>
            <div
              className="cc-sa-source-split-panel cc-sa-source-split-panel--side-by-side"
              role="region"
              aria-label="Sales Agreement source PDF"
            >
              <div className="cc-sa-source-split-header">
                <span className="cc-sa-source-split-title">
                  Sales Agreement
                  {salesAgreementSourceView.clauseNumberDisplay
                    ? ` — Clause ${salesAgreementSourceView.clauseNumberDisplay}`
                    : ''}
                </span>
                <div className="cc-sa-source-split-header-actions">
                  <button
                    type="button"
                    className="cc-sa-source-split-close"
                    onClick={() => handleCloseSalesAgreementSource({ alignClause: true })}
                    aria-label="Close Sales Agreement viewer"
                    title="Close Sales Agreement viewer"
                  >
                    ✕
                  </button>
                </div>
              </div>
              <div className="cc-sa-source-split-body">
                {salesAgreementSourceView.loading && (
                  <div className="cc-source-loading" role="status" aria-live="polite">
                    <span className="cc-source-loading-spinner" aria-hidden />
                    <p className="cc-source-loading-text">Loading Sales Agreement…</p>
                  </div>
                )}
                {salesAgreementSourceView.error && (
                  <p className="cc-source-pdf-error">{salesAgreementSourceView.error}</p>
                )}
                {salesAgreementSourceView.buffer &&
                  !salesAgreementSourceView.loading &&
                  !salesAgreementSourceView.error && (
                    <PastNegotiationSourcePdfPanel
                      pdfData={salesAgreementSourceView.buffer}
                      initialPage={salesAgreementSourceView.page}
                      totalPagesHint={salesAgreementSourceView.total}
                      searchText={salesAgreementSourceView.searchText}
                      clauseNumber={salesAgreementSourceView.clauseNumber}
                    />
                  )}
              </div>
            </div>

            {!showBesidePortal && besideResizeHandleEl}
          </>
        ))}

      {playbookAgentBeside &&
        (showBesidePortal ? (
          <>
            {createPortal(
              <div className="cc-sa-mcc-portal-pdf-root">
                <div
                  className="cc-sa-source-split-panel cc-sa-source-split-panel--side-by-side cc-sa-source-split-panel--mcc-docked cc-sa-source-split-panel--playbook-agent"
                  role="region"
                  aria-label="Playbook agent chat"
                >
                  <div className="cc-sa-source-split-header">
                    <span className="cc-sa-source-split-title">
                      Ask Agent
                      {playbookAgentBeside.clauseNumberDisplay
                        ? ` — Clause ${playbookAgentBeside.clauseNumberDisplay}`
                        : ''}
                    </span>
                    <div className="cc-sa-source-split-header-actions">
                      <button
                        type="button"
                        className="cc-sa-source-split-close"
                        onClick={() => handleClosePlaybookAgent({ alignClause: true })}
                        aria-label="Close playbook agent"
                        title="Close playbook agent"
                      >
                        ✕
                      </button>
                    </div>
                  </div>
                  <div className="cc-sa-source-split-body">
                    {playbookAgentBeside.loading && (
                      <div className="cc-source-loading" role="status" aria-live="polite">
                        <span className="cc-source-loading-spinner" aria-hidden />
                        <p className="cc-source-loading-text">Loading playbook…</p>
                      </div>
                    )}
                    {playbookAgentBeside.error && !playbookAgentBeside.loading && (
                      <p className="cc-source-pdf-error">{playbookAgentBeside.error}</p>
                    )}
                    {!playbookAgentBeside.loading &&
                      !playbookAgentBeside.error &&
                      (playbookAgentBeside.contextBody ||
                        playbookAgentBeside.readOnly) && (
                        <PlaybookAgentBesidePanel
                          key={playbookAgentBeside.sessionKey}
                          projectLabel={playbookAgentBeside.projectLabel}
                          recordCount={playbookAgentBeside.recordCount}
                          contextBody={playbookAgentBeside.contextBody}
                          clauseNumberDisplay={playbookAgentBeside.clauseNumberDisplay}
                          askLanguage={playbookAgentBeside.askLanguage}
                          initialQuestion={playbookAgentBeside.initialQuestion}
                          openingAssistantMessage={playbookAgentBeside.openingAssistantMessage}
                          awaitingDeepResearchConsent={
                            playbookAgentBeside.awaitingDeepResearchConsent
                          }
                          awaitingContextSourceChoice={
                            playbookAgentBeside.awaitingContextSourceChoice
                          }
                          contextSource={playbookAgentBeside.contextSource}
                          onChooseContextSource={
                            playbookAgentBeside.readOnly
                              ? undefined
                              : handleChooseAskAgentContextSource
                          }
                          playbookRows={playbookAgentBeside.playbookRows}
                          matchRankToArchiveRecordNum={
                            playbookAgentBeside.matchRankToArchiveRecordNum
                          }
                          projectId={projectId ?? projectIdProp ?? null}
                          playbookDomain={detectedDomain ?? 'construction-loan'}
                          clauseContextSnippet={playbookAgentBeside.contextBody.slice(0, 2000)}
                          onApplySuggestedEdit={
                            playbookAgentBeside.readOnly
                              ? undefined
                              : handleApplyAgentSuggestedEdit
                          }
                          isApplyingSuggestedEdit={
                            agentAiStreamingClauseId === playbookAgentBeside.clauseId
                          }
                          readOnly={playbookAgentBeside.readOnly}
                          initialMessages={playbookAgentBeside.initialMessages}
                        />
                      )}
                  </div>
                </div>
              </div>,
              pdfBesideDockHost!,
            )}
            {!salesAgreementSourceView && renderBesideResizeHandlePortal(besideHandleSlotHost)}
          </>
        ) : (
          <>
            <div
              className="cc-sa-source-split-panel cc-sa-source-split-panel--side-by-side cc-sa-source-split-panel--playbook-agent"
              role="region"
              aria-label="Playbook agent chat"
            >
              <div className="cc-sa-source-split-header">
                <span className="cc-sa-source-split-title">
                  Ask Agent
                  {playbookAgentBeside.clauseNumberDisplay
                    ? ` — Clause ${playbookAgentBeside.clauseNumberDisplay}`
                    : ''}
                </span>
                <div className="cc-sa-source-split-header-actions">
                  <button
                    type="button"
                    className="cc-sa-source-split-close"
                    onClick={() => handleClosePlaybookAgent({ alignClause: true })}
                    aria-label="Close playbook agent"
                    title="Close playbook agent"
                  >
                    ✕
                  </button>
                </div>
              </div>
              <div className="cc-sa-source-split-body">
                {playbookAgentBeside.loading && (
                  <div className="cc-source-loading" role="status" aria-live="polite">
                    <span className="cc-source-loading-spinner" aria-hidden />
                    <p className="cc-source-loading-text">Loading playbook…</p>
                  </div>
                )}
                {playbookAgentBeside.error && !playbookAgentBeside.loading && (
                  <p className="cc-source-pdf-error">{playbookAgentBeside.error}</p>
                )}
                  {!playbookAgentBeside.loading &&
                  !playbookAgentBeside.error &&
                  (playbookAgentBeside.contextBody || playbookAgentBeside.readOnly) && (
                    <PlaybookAgentBesidePanel
                      key={playbookAgentBeside.sessionKey}
                      projectLabel={playbookAgentBeside.projectLabel}
                      recordCount={playbookAgentBeside.recordCount}
                      contextBody={playbookAgentBeside.contextBody}
                      clauseNumberDisplay={playbookAgentBeside.clauseNumberDisplay}
                      askLanguage={playbookAgentBeside.askLanguage}
                      initialQuestion={playbookAgentBeside.initialQuestion}
                      openingAssistantMessage={playbookAgentBeside.openingAssistantMessage}
                      awaitingDeepResearchConsent={
                        playbookAgentBeside.awaitingDeepResearchConsent
                      }
                      awaitingContextSourceChoice={
                        playbookAgentBeside.awaitingContextSourceChoice
                      }
                      contextSource={playbookAgentBeside.contextSource}
                      onChooseContextSource={
                        playbookAgentBeside.readOnly
                          ? undefined
                          : handleChooseAskAgentContextSource
                      }
                      playbookRows={playbookAgentBeside.playbookRows}
                      matchRankToArchiveRecordNum={
                        playbookAgentBeside.matchRankToArchiveRecordNum
                      }
                      projectId={projectId ?? projectIdProp ?? null}
                      playbookDomain={detectedDomain ?? 'construction-loan'}
                      clauseContextSnippet={playbookAgentBeside.contextBody.slice(0, 2000)}
                      onApplySuggestedEdit={
                        playbookAgentBeside.readOnly
                          ? undefined
                          : handleApplyAgentSuggestedEdit
                      }
                      isApplyingSuggestedEdit={
                        agentAiStreamingClauseId === playbookAgentBeside.clauseId
                      }
                      readOnly={playbookAgentBeside.readOnly}
                      initialMessages={playbookAgentBeside.initialMessages}
                    />
                  )}
              </div>
            </div>
            {!showBesidePortal && besideResizeHandleEl}
          </>
        ))}

      <div
        ref={resultsMainColumnRef}
        className={`cc-results-main-column${
          besidePanelOpen
            ? ` cc-results-main-column--side-by-side${
                useTaskPaneBesidePortalLayout ? ' cc-results-main-column--mcc-beside-portal' : ''
              }`
            : ' cc-results-main-column--stacked'
        }`}
      >
      {/* Scrolls away with page (same level as Project Settings in negotiator layout) */}
      <div ref={workflowStepsScrollAnchorRef} className="cc-results-steps-scroll">
        <div className="cc-run-steps-panel" dir={chromeDir}>
          <div className={`cc-run-step-item cc-run-${stepStatuses.step0}`}>
            <span className="cc-run-arrow" aria-hidden>&#8250;</span>
            <span className="cc-run-status-icon" aria-hidden>✓</span>
            <span className="cc-run-label">{t('results.steps.identifySides')}</span>
            <span className="cc-run-timer" />
          </div>
          <div className={`cc-run-step-item cc-run-${stepStatuses.step1}`}>
            <span className="cc-run-arrow" aria-hidden>&#8250;</span>
            <span className="cc-run-status-icon" aria-hidden>
              {stepStatuses.step1 === 'complete' ? '✓' : stepStatuses.step1 === 'running' ? '◌' : stepStatuses.step1 === 'error' ? '✕' : '○'}
            </span>
            <span className="cc-run-label">{t('results.steps.identifyClientRequests')}</span>
            <span className="cc-run-timer">{stepTimes.step1}</span>
          </div>
          <div className={`cc-run-step-item cc-run-${stepStatuses.step2}`}>
            <span className="cc-run-arrow" aria-hidden>&#8250;</span>
            <span className="cc-run-status-icon" aria-hidden>
              {stepStatuses.step2 === 'complete' ? '✓' : stepStatuses.step2 === 'running' ? '◌' : stepStatuses.step2 === 'error' ? '✕' : '○'}
            </span>
            <span className="cc-run-label">{t('results.steps.crossCheckAnalysis')}</span>
            <span className="cc-run-timer">{stepTimes.step2}</span>
          </div>
          {agentAutoApplyEnabled &&
            AGENT_PHASE_ORDER.map((phaseKey) => {
              const rawPhase = agentPhaseProgress[phaseKey];
              // The pipeline is armed but has not reported yet: show Ask Agent
              // as running rather than as an idle circle, which is how it used
              // to look for the two minutes before the first batch went out.
              const isPreparing =
                agentAutoApplyArmed && phaseKey === 'asking' && rawPhase.status === 'pending';
              const phase: AgentPhaseState = isPreparing
                ? { ...rawPhase, status: 'running' }
                : rawPhase;
              const pct = agentPhasePercent(phase);
              const labelKey =
                phaseKey === 'asking'
                  ? 'results.steps.agentAsk'
                  : phaseKey === 'editing'
                    ? 'results.steps.agentEdits'
                    : 'results.steps.agentSubmit';
              // Only the submitting step can currently have per-clause failures
              // (a generated edit that never made it into Word).
              const failuresForPhase = phaseKey === 'submitting' ? agentSubmitFailures : [];
              const hasFailures = failuresForPhase.length > 0;
              const isExpanded = expandedAgentPhase === phaseKey && hasFailures;

              return (
                <React.Fragment key={phaseKey}>
                  <div
                    className={`cc-run-step-item cc-run-${phase.status}${hasFailures ? ' cc-run-has-warnings' : ''}`}
                    data-agent-phase={phaseKey}
                    role={hasFailures ? 'button' : undefined}
                    tabIndex={hasFailures ? 0 : undefined}
                    aria-expanded={hasFailures ? isExpanded : undefined}
                    onClick={hasFailures ? () => setExpandedAgentPhase(isExpanded ? null : phaseKey) : undefined}
                    onKeyDown={
                      hasFailures
                        ? (e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              setExpandedAgentPhase(isExpanded ? null : phaseKey);
                            }
                          }
                        : undefined
                    }
                  >
                    <span
                      className={`cc-run-arrow${isExpanded ? ' cc-run-arrow-expanded' : ''}`}
                      aria-hidden
                    >
                      &#8250;
                    </span>
                    {hasFailures ? (
                      <span
                        className="cc-run-status-icon cc-run-warn-icon"
                        aria-hidden
                        title={t('results.agentSubmitErrors.tooltip')}
                      >
                        !
                      </span>
                    ) : (
                      <span className="cc-run-status-icon" aria-hidden>
                        {phase.status === 'complete'
                          ? '✓'
                          : phase.status === 'running'
                            ? '◌'
                            : phase.status === 'error'
                              ? '✕'
                              : phase.status === 'paused'
                                ? '⏸'
                                : '○'}
                      </span>
                    )}
                    <span className="cc-run-label">{t(labelKey)}</span>
                    <span
                      className="cc-run-timer cc-run-progress"
                      dir={isPreparing ? undefined : 'ltr'}
                    >
                      {isPreparing
                        ? t('results.steps.agentPreparing')
                        : phase.status === 'pending'
                          ? ''
                          : phase.total > 0
                            ? `${pct}% · ${phase.current}/${phase.total}`
                            : `${pct}%`}
                    </span>
                  </div>
                  {isExpanded && (
                    <div className="cc-run-warn-list" role="list">
                      {failuresForPhase.map((f) => (
                        <div
                          key={f.clauseId}
                          className="cc-run-warn-row"
                          role="button"
                          tabIndex={0}
                          onClick={(e) => {
                            e.stopPropagation();
                            revealAndScrollStickyClause(f.clauseId);
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              e.stopPropagation();
                              revealAndScrollStickyClause(f.clauseId);
                            }
                          }}
                        >
                          <span className="cc-run-warn-row-icon" aria-hidden>!</span>
                          <span className="cc-run-warn-row-text">
                            {t('results.agentSubmitErrors.notApplied', {
                              clause: f.clauseNumber
                                ? formatClauseNumberForDisplay(f.clauseNumber)
                                : f.clauseId,
                            })}
                            {' — '}
                            {t(`results.agentSubmitErrors.reason.${f.error}`, {
                              defaultValue: f.error,
                            })}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </React.Fragment>
              );
            })}
        </div>
        {agentAutoApplyEnabled && agentPause && (
          <div className="cc-agent-usage-limit-banner" dir={chromeDir} role="alert">
            <span className="cc-agent-usage-limit-banner-icon" aria-hidden>⏸</span>
            <div className="cc-agent-usage-limit-banner-text">
              <div className="cc-agent-usage-limit-banner-title">
                {agentPause.reason === 'session_expired'
                  ? t('results.agentUsageLimitPause.titleSessionExpired')
                  : t('results.agentUsageLimitPause.title')}
              </div>
              <div className="cc-agent-usage-limit-banner-message">{agentPause.message}</div>
              <div className="cc-agent-usage-limit-banner-hint">
                {t('results.agentUsageLimitPause.resumeHint')}
              </div>
            </div>
            <button
              type="button"
              className="cc-agent-usage-limit-banner-resume"
              onClick={resumeAgentAutoApplyAfterPause}
            >
              {t('results.agentUsageLimitPause.resume')}
            </button>
          </div>
        )}
      </div>

      {/* Sticky: progress + tabs only (matches ClausesEditor sticky header pattern).
          Hidden whenever there is no card for it to count or filter — while a
          step is still running, and on a project with no results at all.
          `hidden` rather than unmounted so the scroll anchoring below keeps its
          element across the transition. */}
      <div
        className="cc-results-sticky-header"
        ref={stickyHeaderRef}
        dir={chromeDir}
        hidden={awaitingResults || clauses.length === 0}
      >
        <div className="cc-results-header">
          {brainShareTotalCount > 0 && (
            <button
              type="button"
              className="cc-brainshare-header-btn"
              onClick={() => setShowBrainShareReviewModal(true)}
              title={
                brainSharePendingCount > 0
                  ? t('results.brainShare.pending', { count: brainSharePendingCount })
                  : t('results.brainShare.view')
              }
              aria-label={t('results.brainShare.view')}
            >
              <Brain className="cc-brainshare-header-btn-icon" aria-hidden />
              {brainSharePendingCount > 0 && (
                <span className="cc-brainshare-header-badge">
                  {brainSharePendingCount > 99 ? '99+' : brainSharePendingCount}
                </span>
              )}
            </button>
          )}
          <div className="cc-results-progress">
            <div className="cc-results-progress-bar">
              <div
                className="cc-results-progress-fill"
                style={{
                  width: progress.total > 0 ? `${progress.pct}%` : '0%',
                }}
              />
            </div>
            <div className="cc-results-progress-text">
              {t('results.progress', {
                current: progress.current,
                total: progress.total,
                pct: progress.pct,
              })}
            </div>
          </div>
          <div className="cc-results-header-actions">
            <button
              className="cc-refresh-btn"
              onClick={() => { void loadResults(); }}
              disabled={loading}
              title={t('results.refresh')}
            >
              &#x21BB;
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="cc-results-tabs">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              type="button"
              className={`cc-tab ${tab.cssClass}${activeTab === tab.id ? ' cc-tab-active' : ''}`}
              onClick={() => handleTabClick(tab.id)}
              aria-pressed={activeTab === tab.id}
              aria-label={t('results.showTab', { label: tab.label })}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {showClauseSubFilters && (
          <div className="cc-results-subfilters-wrap">
            <div className="cc-results-subfilters">
              <div className="cc-results-subfilters-main">
                <span className="cc-results-subfilters-label">{t('results.filters.label')}</span>
                <div className="cc-results-subfilters-chips" role="group" aria-label={t('results.filters.clauseFilters')}>
                  <button
                    type="button"
                    className={`cc-subfilter-chip${filterFinalVersion ? ' cc-subfilter-chip--active' : ''}`}
                    onClick={() => setFilterFinalVersion((prev) => !prev)}
                    aria-pressed={filterFinalVersion}
                  >
                    {t('results.filters.finalVersion')}
                  </button>
                  <div className="cc-similarity-filter" ref={similarityFilterRef}>
                    <button
                      type="button"
                      className={`cc-subfilter-chip cc-subfilter-chip--similarity${
                        filterMinSimilarity != null || similarityPanelOpen ? ' cc-subfilter-chip--active' : ''
                      }`}
                      onClick={handleSimilarityChipClick}
                      aria-pressed={filterMinSimilarity != null || similarityPanelOpen}
                      aria-expanded={similarityPanelOpen}
                    >
                      {t('results.filters.similarity')}
                      {filterMinSimilarity != null && (
                        <span className="cc-subfilter-chip-value">
                          {formatSimilarityFilterLabel(filterMinSimilarity)}
                        </span>
                      )}
                    </button>
                    {similarityPanelOpen && (
                      <div className="cc-similarity-panel" role="dialog" aria-label={t('results.filters.similarity')}>
                        <div className="cc-similarity-panel-header">
                          <span className="cc-similarity-panel-title">{t('results.filters.minSimilarity')}</span>
                          <span className="cc-similarity-panel-value">
                            {formatSimilarityFilterLabel(similaritySliderValue)}
                          </span>
                        </div>
                        <input
                          type="range"
                          className="cc-similarity-slider"
                          min={SIMILARITY_FILTER_MIN}
                          max={SIMILARITY_FILTER_MAX}
                          step={1}
                          value={similaritySliderValue}
                          onChange={handleSimilaritySliderChange}
                          aria-label={t('results.filters.minSimilarityAria')}
                          aria-valuemin={SIMILARITY_FILTER_MIN}
                          aria-valuemax={SIMILARITY_FILTER_MAX}
                          aria-valuenow={similaritySliderValue}
                        />
                        <div className="cc-similarity-presets" role="group" aria-label={t('results.filters.similarityQuick')}>
                          {SIMILARITY_FILTER_PRESETS.map((preset) => (
                            <button
                              key={preset}
                              type="button"
                              className={`cc-similarity-preset${
                                filterMinSimilarity === preset ? ' cc-similarity-preset--active' : ''
                              }`}
                              onClick={() => applySimilarityThreshold(preset)}
                              aria-pressed={filterMinSimilarity === preset}
                            >
                              {formatSimilarityFilterLabel(preset)}
                            </button>
                          ))}
                        </div>
                        {filterMinSimilarity != null && (
                          <button
                            type="button"
                            className="cc-similarity-clear"
                            onClick={handleClearSimilarityFilter}
                          >
                            {t('results.filters.clearSimilarity')}
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                  {availableAuthors.length > 0 && (
                    <div className="cc-authors-filter" ref={authorsFilterRef}>
                      <button
                        type="button"
                        className={`cc-subfilter-chip cc-subfilter-chip--authors${
                          filterSelectedAuthors !== null || authorsPanelOpen ? ' cc-subfilter-chip--active' : ''
                        }`}
                        onClick={handleAuthorsChipClick}
                        aria-pressed={filterSelectedAuthors !== null || authorsPanelOpen}
                        aria-expanded={authorsPanelOpen}
                      >
                        {t('results.filters.authors')}
                        {authorFilterLabel && (
                          <span className="cc-subfilter-chip-value">{authorFilterLabel}</span>
                        )}
                      </button>
                      {authorsPanelOpen && (
                        <div className="cc-authors-panel" role="dialog" aria-label={t('results.filters.authors')}>
                          <div className="cc-authors-panel-header">
                            <span className="cc-authors-panel-title">{t('results.filters.pastNegotiationAuthors')}</span>
                          </div>
                          <button
                            type="button"
                            className={`cc-authors-all-btn${
                              filterSelectedAuthors === null ? ' cc-authors-all-btn--active' : ''
                            }`}
                            onClick={handleSelectAllAuthors}
                            aria-pressed={filterSelectedAuthors === null}
                          >
                            {t('results.filters.all')}
                          </button>
                          <div className="cc-authors-list" role="group" aria-label={t('results.filters.selectAuthors')}>
                            {availableAuthors.map((author) => {
                              const isChecked =
                                filterSelectedAuthors === null || filterSelectedAuthors.has(author);
                              return (
                                <label key={author} className="cc-authors-option">
                                  <input
                                    type="checkbox"
                                    checked={isChecked}
                                    onChange={() => handleToggleAuthorFilter(author)}
                                  />
                                  <span className="cc-authors-option-label">{author}</span>
                                </label>
                              );
                            })}
                          </div>
                          {filterSelectedAuthors !== null && (
                            <button
                              type="button"
                              className="cc-authors-clear"
                              onClick={handleClearAuthorsFilter}
                            >
                              {t('results.filters.clearAuthors')}
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                  {availableYears.length > 0 && (
                    <div className="cc-time-filter" ref={timeFilterRef}>
                      <button
                        type="button"
                        className={`cc-subfilter-chip cc-subfilter-chip--time${
                          filterSelectedYears !== null || timePanelOpen ? ' cc-subfilter-chip--active' : ''
                        }`}
                        onClick={handleTimeChipClick}
                        aria-pressed={filterSelectedYears !== null || timePanelOpen}
                        aria-expanded={timePanelOpen}
                      >
                        {t('results.filters.time')}
                        {timeFilterLabel && (
                          <span className="cc-subfilter-chip-value">{timeFilterLabel}</span>
                        )}
                      </button>
                      {timePanelOpen && (
                        <div className="cc-time-panel" role="dialog" aria-label={t('results.filters.time')}>
                          <div className="cc-time-panel-header">
                            <span className="cc-time-panel-title">{t('results.filters.pastNegotiationTime')}</span>
                          </div>
                          <button
                            type="button"
                            className={`cc-time-all-btn${
                              filterSelectedYears === null ? ' cc-time-all-btn--active' : ''
                            }`}
                            onClick={handleSelectAllTime}
                            aria-pressed={filterSelectedYears === null}
                          >
                            {t('results.filters.all')}
                          </button>
                          <div className="cc-authors-list" role="group" aria-label={t('results.filters.selectYears')}>
                            {availableYears.map((year) => {
                              const isChecked =
                                filterSelectedYears === null || filterSelectedYears.has(year);
                              return (
                                <label key={year} className="cc-authors-option">
                                  <input
                                    type="checkbox"
                                    checked={isChecked}
                                    onChange={() => handleToggleYearFilter(year)}
                                  />
                                  <span className="cc-authors-option-label">{year}</span>
                                </label>
                              );
                            })}
                          </div>
                          {filterSelectedYears !== null && (
                            <button
                              type="button"
                              className="cc-time-clear"
                              onClick={handleClearTimeFilter}
                            >
                              {t('results.filters.clearTime')}
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                  {hasActiveSubFilters && (
                    <button
                      type="button"
                      className="cc-subfilter-clear"
                      onClick={handleClearSubFilters}
                      aria-label={t('results.filters.clearFilters')}
                    >
                      {t('results.filters.clear')}
                    </button>
                  )}
                </div>
                {hasActiveClauseSubFilters && (
                  <span className="cc-results-subfilters-count">
                    {t('results.filters.showing', {
                      shown: displayedClauses.length,
                      total: filteredClauses.length,
                    })}
                  </span>
                )}
              </div>
              {hasActiveClauseSubFilters && (
                <div className="cc-results-subfilters-actions">
                  <button
                    type="button"
                    className="cc-subfilter-open-btn"
                    onClick={handleOpenAllFiltered}
                    disabled={bulkOpening || bulkClosing || bulkClosePrompt != null || openableCount === 0}
                    title={t('results.filters.openAllTitle')}
                    aria-label={t('results.filters.openAll', { count: openableCount })}
                  >
                    <svg
                      className="cc-subfilter-open-btn-icon"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      aria-hidden
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M8 11V7a4 4 0 118 0m-4 8v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2z"
                      />
                    </svg>
                    {bulkOpening ? t('results.filters.opening') : t('results.filters.openAll', { count: openableCount })}
                  </button>
                  <button
                    type="button"
                    className="cc-subfilter-close-btn"
                    onClick={handleCloseFiltered}
                    disabled={bulkClosing || bulkOpening || bulkClosePrompt != null || closableCount === 0}
                    title={t('results.filters.closeTitle')}
                    aria-label={t('results.filters.closeCount', { count: closableCount })}
                  >
                    <svg
                      className="cc-subfilter-close-btn-lock"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                      aria-hidden
                    >
                      <path
                        fillRule="evenodd"
                        d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                    {bulkClosing ? t('results.filters.closing') : t('results.filters.closeCount', { count: closableCount })}
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Overlay sync indicator — does not shift clause cards */}
        {syncingIndices && (
          <div className="cc-sync-banner cc-sync-banner--overlay" role="status" aria-live="polite">
            <span className="cc-sync-banner-spinner" aria-hidden />
            <span className="cc-sync-banner-text">{t('results.syncing')}</span>
          </div>
        )}
      </div>

      {showDebugLogPanel && (
        <div className="cc-debug-log-bar" role="region" aria-label="Debug logging">
          <div className="cc-debug-log-actions">
            <span className="cc-debug-log-meta">
              Session {getRemoteDebugSessionId()} · {remoteDebugEntryCount} entries
            </span>
            <button
              type="button"
              className="cc-debug-log-copy"
              disabled={remoteDebugCopying || remoteDebugEntryCount === 0}
              onClick={async () => {
                setRemoteDebugCopying(true);
                setRemoteDebugCopyStatus(null);
                try {
                  const result = await copyDebugLogToClipboard();
                  if (result.ok) {
                    setRemoteDebugCopyStatus(
                      `Copied ${result.charCount?.toLocaleString() ?? ''} characters — paste into your email.`,
                    );
                  } else {
                    setRemoteDebugCopyStatus(result.error ?? 'Copy failed.');
                  }
                } finally {
                  setRemoteDebugCopying(false);
                }
              }}
            >
              {remoteDebugCopying ? 'Copying…' : 'Copy logs'}
            </button>
            <button
              type="button"
              className="cc-debug-log-clear"
              onClick={() => {
                clearDebugLog();
                setRemoteDebugEntryCount(0);
                setRemoteDebugCopyStatus(null);
              }}
            >
              Clear
            </button>
          </div>
          {remoteDebugCopyStatus && (
            <div className="cc-debug-log-status" role="status">
              {remoteDebugCopyStatus}
            </div>
          )}
          <p className="cc-debug-log-hint">
            Reproduce the issue, click Copy logs, then paste into an email to support. Turn off in
            Account → Debug logging.
          </p>
        </div>
      )}

      {/* Error */}
      {error && <div className="cc-error">{error}</div>}

      {/* Subtle reconnecting indicator shown during transient poll failures (results stay visible) */}
      {pollingError && !error && (
        <div className="cc-polling-error">
          <span className="cc-action-banner-spinner" aria-hidden />
          <span>Reconnecting…</span>
        </div>
      )}

      {/* Polling stopped banner — shown after too many consecutive errors; lets user restart polling */}
      {pollingStopped && !error && (
        <div className="cc-action-banner cc-action-banner--error" role="status">
          <span>Updates paused due to connection issues.</span>
          <button
            className="cc-retry-poll-btn"
            onClick={() => {
              setPollingStopped(false);
              setPollingError(false);
              setPollRetryKey((k) => k + 1);
            }}
          >
            Retry
          </button>
        </div>
      )}

      {actionBanner && (
        <div
          className={`cc-action-banner cc-action-banner--${actionBanner.type}`}
          role="status"
        >
          {actionBanner.type === 'loading' && <span className="cc-action-banner-spinner" aria-hidden />}
          <span>{actionBanner.message}</span>
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div className="cc-loading" dir={chromeDir}>
          <div className="cc-spinner" />
          <span className="cc-loading-text">{t('results.loading')}</span>
          <span className="cc-loading-sub">{t('results.loadingHint')}</span>
        </div>
      )}

      {/* A step is still running — say which, instead of an empty tab bar or cards
          whose findings have not arrived yet */}
      {!loading && awaitingStage && (
        <div className="cc-awaiting-results" dir={chromeDir} role="status" aria-live="polite">
          <div className="cc-spinner" />
          <span className="cc-awaiting-results-title">
            {awaitingStage === 'identify'
              ? t('results.awaitingResults.identify.title')
              : t('results.awaitingResults.crossCheck.title')}
          </span>
          <span className="cc-awaiting-results-hint">
            {awaitingStage === 'identify'
              ? t('results.awaitingResults.identify.hint')
              : clauses.length > 0
                ? t('results.awaitingResults.crossCheck.hintWithClauses', { count: clauses.length })
                : t('results.awaitingResults.crossCheck.hint')}
          </span>
        </div>
      )}

      {/* Empty state */}
      {!loading && !error && !awaitingResults && displayedClauses.length === 0 && (
        <div className="cc-empty-state" dir={chromeDir}>
          <span className="cc-empty-icon">&#128196;</span>
          <span className="cc-empty-title">
            {clauses.length === 0
              ? t('results.empty.noResults')
              : hasActiveSubFilters && filteredClauses.length > 0
              ? t('results.empty.noFilterMatch')
              : t('results.empty.noCategory', {
                  category:
                    activeTab === 'sales_agreement'
                      ? t('results.empty.categorySalesAgreement')
                      : activeTab === 'appendix_a'
                      ? t('results.empty.categoryAppendixA')
                      : activeTab === 'saved'
                      ? t('results.empty.categorySaved')
                      : activeTab === 'commercial'
                      ? t('results.empty.categoryCommercial')
                      : activeTab === 'legal'
                      ? t('results.empty.categoryLegal')
                      : activeTab === 'other'
                      ? t('results.empty.categoryOther')
                      : '',
                })}
          </span>
          {hasActiveSubFilters && filteredClauses.length > 0 && (
            <button
              type="button"
              className="cc-subfilter-clear cc-subfilter-clear--empty"
              onClick={handleClearSubFilters}
            >
              {t('results.filters.clearFilters')}
            </button>
          )}
          {clauses.length === 0 && (
            <span className="cc-empty-subtitle">
              {workflowStep == null && !workflowProgressBlob
                ? t('results.empty.notStarted')
                : t('results.empty.runInDashboard')}
            </span>
          )}
        </div>
      )}

      {/* Clause cards — key by activeTab so list remounts and updates when filter changes */}
      {!loading && !awaitingResults && displayedClauses.length > 0 && (
        <div
          key={`${activeTab}-${filterFinalVersion}-${filterMinSimilarity ?? 'any'}`}
          className="cc-results-list"
          role="list"
        >
          {displayedClauses.map((clause, idx) => {
            const isSaSourceActive =
              !!salesAgreementSourceView && salesAgreementSourceView.clauseId === clause.id;
            const isSaSourceLoading =
              isSaSourceActive && !!salesAgreementSourceView?.loading;
            return (
              <CrossCheckClauseCard
                key={clause.id}
                clause={clause}
                index={idx}
                projectId={projectId ?? projectIdProp}
                subProjectId={subProjectId}
                onToggleLock={handleToggleLock}
                isUpdatingLock={updatingLockId === clause.id}
                onClauseEdited={handleClauseEdited}
                onActionBanner={handleActionBanner}
                onBrainShareUpdated={handleBrainShareUpdated}
                onResponseSubmitted={handleResponseSubmitted}
                onResponseUndone={handleResponseUndone}
                submitRevertContextForClause={
                  submitRevertContextByClauseId[clause.id] ??
                  submitRevertContextFromSubmissionStatus(clause.submission_status)
                }
                pastAgreementPlaybookXmlById={playbookXmlById}
                pastAgreementPlaybookSideById={playbookSideById}
                pastAgreementPlaybookFileNameById={playbookFileNameById}
                pastAgreementPlaybookFileIdById={playbookFileIdById}
                pastAgreementPlaybookFinalVersionById={playbookFinalVersionById}
                pastAgreementPlaybookPlainTextById={playbookPlainTextById}
                pastAgreementPlaybookAuthorById={playbookAuthorById}
                pastAgreementAuthorFilter={filterSelectedAuthors}
                pastAgreementPlaybookTimestampById={playbookTimestampById}
                pastAgreementTimeFilter={filterSelectedYears}
                pastAgreementPlaybooksLoading={playbooksBatchLoading}
                pastAgreementDomain={detectedDomain ?? 'construction-loan'}
                onGoToSalesAgreementSource={handleOpenSalesAgreementSource}
                isSalesAgreementSourceActive={isSaSourceActive}
                isSalesAgreementSourceLoading={isSaSourceLoading}
                kebabVariant={isConstructionLoan ? 'construction-loan' : 'mcc'}
                crossCheckSolution={crossCheckSolution}
                onMoveClause={isConstructionLoan ? undefined : handleMoveClause}
                onMoveClauseMatter={isConstructionLoan ? handleMoveClauseMatter : undefined}
                onAnalyzeAgain={handleAnalyzeAgainOpen}
                onAskAgent={projectId ?? projectIdProp ? handleOpenPlaybookAgent : undefined}
                onOpenAgentAutoResult={
                  clause.ask_agent_auto_result ? handleOpenAgentAutoResult : undefined
                }
                isMutating={!!mutatingClauseIds[clause.id]}
                externalAiStreaming={agentAiStreamingClauseId === clause.id}
                externalEditedXml={
                  agentSuggestedXmlByClauseId[clause.id] ??
                  clause.ask_agent_auto_result?.suggested_xml ??
                  null
                }
                cardHeaderRef={setClauseCardHeaderRef(clause.id)}
                isStickyFocused={stickyHighlightedClauseId === clause.id}
              />
            );
          })}
        </div>
      )}
      </div>

      {bulkClosePrompt && (
        <BulkCloseConfirmModal
          trackChangesCount={bulkClosePrompt.withTrackChanges.length}
          totalCount={bulkClosePrompt.toLock.length}
          busy={bulkClosing}
          busyMessage={bulkCloseBusyMessage}
          onAcceptAndClose={() => { void handleBulkCloseAcceptAndClose(); }}
          onCloseWithoutAccept={() => { void handleBulkCloseWithoutAccept(); }}
          onCancel={() => {
            if (bulkClosing) return;
            setBulkClosePrompt(null);
            setBulkCloseBusyMessage(null);
          }}
        />
      )}

      <AnalyzeAgainModal
        clause={analyzeAgainClause}
        variant={analyzeAgainVariant}
        onCancel={() => setAnalyzeAgainClause(null)}
        onSubmit={handleAnalyzeAgainSubmit}
      />

      {subProjectId && (
        <BrainShareReviewModal
          isOpen={showBrainShareReviewModal}
          onClose={() => setShowBrainShareReviewModal(false)}
          subProjectId={subProjectId}
          onCountsChanged={() => { void fetchBrainShareCounts(); }}
          onNotify={handleActionBanner}
        />
      )}
    </div>
  );
};

export default CrossCheckResultsView;
